
/** Base de la fenetre : billet (defaut) ou dates de l'exposition. */
export type AccessWindowBasis = "VISIT_DATE" | "EXHIBITION_DATES";

/**
 * Client API pour le back-office Alto.
 *
 * Convention :
 *   - le token JWT est stocké en localStorage sous la clé `alto_admin_token`
 *   - toutes les requêtes admin attachent `Authorization: Bearer <token>`
 *   - 401 → on purge le token et on redirige vers /login
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000";
const TOKEN_KEY = "alto_admin_token";
const TENANT_KEY = "alto_admin_tenant";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  // Reset AUSSI le scope (tenant + etablissement) a la deconnexion : une nouvelle
  // session doit repartir sur la vue par defaut — "Tous les clients" pour un
  // SUPER_ADMIN — et pas sur le dernier client selectionne. Avant, seule la
  // deconnexion MANUELLE (Layout) vidait le tenant ; les deconnexions par
  // INACTIVITE (useIdleLogout) et 401 EXPIRE ne le faisaient pas -> a la
  // reconnexion on rouvrait sur le dernier client (ex. EPMO) au lieu du global.
  setCurrentTenant(null);
  setCurrentEstablishment(null);
}

/**
 * Redirige proprement vers la page de connexion (jeton expire / 401, ou
 * deconnexion pour inactivite) au lieu de laisser la page courante planter sur
 * une erreur. Redirection "dure" volontaire : etat applicatif repart propre.
 * No-op si on est deja sur /login ou /reset (evite les boucles).
 */
export function redirectToLogin(reason?: "expired" | "idle"): void {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path === "/login" || path === "/reset") return;
  window.location.assign(reason ? `/login?reason=${reason}` : "/login");
}

/**
 * Tenant courant — slug stocké en localStorage, attaché en header
 * `X-Tenant-Slug` automatique sur toutes les requêtes.
 */
export function getCurrentTenant(): string | null {
  return localStorage.getItem(TENANT_KEY);
}

export function setCurrentTenant(slug: string | null): void {
  if (slug) localStorage.setItem(TENANT_KEY, slug);
  else localStorage.removeItem(TENANT_KEY);
}

/**
 * Établissement courant (Sprint M) — slug stocké en localStorage. Pas envoyé
 * automatiquement en header pour l'instant ; consommé par les pages Content
 * (POIs/Parcours) comme valeur par défaut de leur filtre `establishment`.
 */
const ESTABLISHMENT_KEY = "alto_admin_establishment";

export function getCurrentEstablishment(): string | null {
  return localStorage.getItem(ESTABLISHMENT_KEY);
}

/**
 * Le sélecteur du haut de page écrit ici. Un écran qui lit la portée pendant
 * son rendu ne verrait jamais le changement : React n'a aucune raison de le
 * redessiner. D'où l'événement, auquel `useCurrentEstablishment()` s'abonne.
 */
const ESTABLISHMENT_EVENT = "alto:establishment";

export function setCurrentEstablishment(slug: string | null): void {
  if (slug) localStorage.setItem(ESTABLISHMENT_KEY, slug);
  else localStorage.removeItem(ESTABLISHMENT_KEY);
  window.dispatchEvent(new Event(ESTABLISHMENT_EVENT));
}

export function subscribeEstablishment(onChange: () => void): () => void {
  window.addEventListener(ESTABLISHMENT_EVENT, onChange);
  // « storage » couvre le deuxieme onglet du meme back-office.
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(ESTABLISHMENT_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export interface ApiErrorPayload {
  statusCode: number;
  error?: string;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  { auth = true, tenantSlug }: { auth?: boolean; tenantSlug?: string } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  // FormData porte sa propre frontiere multipart : lui imposer un type JSON
  // casse l'envoi de fichier (depot d'un export TMS, 28/10/2026).
  if (init.body && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (auth) {
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    // tenantSlug : override explicite (ex. simulateur qui interroge un tenant
    // autre que le tenant courant). Dans ce cas on ignore le scope établissement
    // global, qui appartiendrait au mauvais tenant.
    const tenant = tenantSlug ?? getCurrentTenant();
    if (tenant) headers.set("X-Tenant-Slug", tenant);
    const est = tenantSlug ? null : getCurrentEstablishment();
    if (est) headers.set("X-Establishment-Slug", est);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as ApiErrorPayload;
      if (body.message) message = body.message;
    } catch {
      /* ignore */
    }
    if (res.status === 401) {
      clearToken();
      // auth=false = requete de login (mauvais mdp) -> pas de redirection (sinon
      // boucle). Sinon = jeton expire/invalide -> retour propre a la connexion.
      if (auth) redirectToLogin("expired");
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// =============================================================================
// Endpoints
// =============================================================================

export type AdminRole = "SUPER_ADMIN" | "ADMIN" | "EDITOR" | "VIEWER" | "SAV" | "COMMERCIAL";

/**
 * V1.51 — droits du back-office, miroir de apps/api/src/lib/permissions.ts.
 * Le serveur envoie ceux du rôle courant (`me().permissions`) ; la Gestion des
 * droits (Administration › Utilisateurs) les règle rôle par rôle.
 */
export type Permission =
  | "content.read"
  | "content.write"
  | "content.publish"
  | "billing.manage"
  | "billing.testCodes"
  | "billing.exempt"
  | "billing.renewUnlimited"
  | "users.manage"
  | "users.manageSuperAdmin"
  | "rights.manage"
  | "rights.manageUser"
  | "mcp.manageTokens"
  | "tenant.manage"
  | "demoAccess.manage"
  | "analytics.read"
  | "analytics.reset"
  | "reporting.finance"
  | "audit.read"
  | "ndp.manage";

export interface AdminMe {
  id: string;
  email: string;
  displayName: string;
  role: AdminRole;
  tenantSlugs: string[];
  currentTenant: string | null;
  /** Sprint Droits B2 : si true, l'accès est bloqué tant que le mdp n'est pas changé. */
  mustChangePassword: boolean;
  /**
   * V1.51 : droits effectifs. 02/11/2026 : ceux de la PERSONNE (son rôle, plus
   * ou moins ses droits propres). Absent d'une API plus ancienne (cf.
   * lib/permissions.ts).
   */
  permissions?: Permission[];
  /** 02/11/2026 : droits de son RÔLE seul, pour distinguer ce qui est propre. */
  rolePermissions?: Permission[];
  /** V1.51 : rôles que ce compte peut donner (pas plus de droits que les siens). */
  assignableRoles?: AdminRole[];
  /** 02/11/2026 : l'appel vient d'un jeton d'API (serveur MCP). */
  viaApiToken?: boolean;
  /** 02/11/2026 : compte Google rattaché, le cas échéant. */
  google?: { email: string | null; pictureUrl: string | null; linkedAt: string | null } | null;
}

export interface LoginResponse {
  token: string;
  expiresIn: string;
  user: AdminMe & { mustChangePassword: boolean };
}

export function login(email: string, password: string) {
  return request<LoginResponse>(
    "/admin/auth/login",
    {
      method: "POST",
      body: JSON.stringify({ email, password }),
    },
    { auth: false },
  );
}

export function me() {
  return request<AdminMe>("/admin/auth/me");
}

// =============================================================================
// V1.51 — Gestion des droits (Administration › Utilisateurs), Super-admin
// =============================================================================

export interface PermissionsPayload {
  /** Rôles, dans l'ordre d'affichage (Super-admin en premier). */
  roles: AdminRole[];
  /** Droits, dans l'ordre du serveur ; `locked` = réservé au Super-admin, non modifiable. */
  permissions: { key: Permission; locked: boolean; defaults: AdminRole[] }[];
  /** Matrice effective : rôles détenteurs, droit par droit (Super-admin toujours inclus). */
  matrix: Record<Permission, AdminRole[]>;
  /** Droits qui s'écartent du réglage par défaut. */
  overridden: Permission[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export function getPermissions() {
  return request<PermissionsPayload>("/admin/permissions");
}

/** Enregistre la matrice ; les droits verrouillés et le Super-admin sont ignorés par le serveur. */
export function savePermissions(matrix: Partial<Record<Permission, AdminRole[]>>) {
  return request<PermissionsPayload & { changed: number }>("/admin/permissions", {
    method: "PUT",
    body: JSON.stringify({ matrix }),
  });
}

/** Revient aux droits par défaut (efface tous les écarts). */
export function resetPermissions() {
  return request<PermissionsPayload & { changed: number }>("/admin/permissions/reset", { method: "POST" });
}

// =============================================================================
// 02/11/2026 — Connexion Google
// =============================================================================

export interface AuthConfig {
  google: { enabled: boolean; allowedDomains: string[] };
}

/** Moyens de connexion ouverts sur ce serveur (public, avant toute session). */
export function getAuthConfig() {
  return request<AuthConfig>("/admin/auth/config", {}, { auth: false });
}

/** Adresse de départ du va-et-vient Google (navigation du navigateur, pas fetch). */
export function googleStartUrl(redirect?: string): string {
  const q = new URLSearchParams();
  if (redirect) q.set("redirect", redirect);
  return `${API_BASE}/admin/auth/google/start${q.size > 0 ? "?" + q.toString() : ""}`;
}

/** Échange le code à usage unique rapporté par Google contre une session. */
export function googleExchange(code: string) {
  return request<LoginResponse & { redirect: string | null }>(
    "/admin/auth/google/exchange",
    { method: "POST", body: JSON.stringify({ code }) },
    { auth: false },
  );
}

/** Détache mon compte Google (la connexion par mot de passe reste). */
export function unlinkMyGoogle() {
  return request<{ ok: true }>("/admin/me/google", { method: "DELETE" });
}

/** Détache le compte Google d'un utilisateur (droit « Gérer les utilisateurs »). */
export function unlinkUserGoogle(id: string) {
  return request<{ ok: true }>(`/admin/users/${id}/google`, { method: "DELETE" });
}

// =============================================================================
// 02/11/2026 — Droits par personne (Administration › Droits par personne)
// =============================================================================

export interface UserRightsOverride {
  permission: string;
  allowed: boolean;
  expiresAt: string | null;
  expired: boolean;
  grantedBy: string | null;
  reason: string | null;
  updatedAt: string;
}

export interface UserRightsRow {
  id: string;
  email: string;
  displayName: string | null;
  role: AdminRole;
  active: boolean;
  lastLoginAt: string | null;
  googleEmail: string | null;
  /** Ligne non modifiable : Super-admin (tout par construction) ou soi-même. */
  readOnly: boolean;
  rolePermissions: Permission[];
  effective: Permission[];
  overrides: UserRightsOverride[];
}

export interface UserRightsPayload {
  roles: AdminRole[];
  permissions: { key: Permission; locked: boolean }[];
  /** Droits que le compte courant peut accorder à quelqu'un d'autre. */
  assignable: Permission[];
  users: UserRightsRow[];
}

export function getUserRights() {
  return request<UserRightsPayload>("/admin/permissions/users");
}

/**
 * Enregistre les droits propres d'une personne.
 * `grants` : droit → true (accordé), false (retiré), null (on suit le rôle).
 */
export function saveUserRights(
  id: string,
  grants: Record<string, boolean | null>,
  options?: { expiresAt?: string | null; reason?: string | null },
) {
  return request<UserRightsPayload & { changed: number; added?: Permission[]; removed?: Permission[] }>(
    `/admin/permissions/users/${id}`,
    { method: "PUT", body: JSON.stringify({ grants, ...options }) },
  );
}

// =============================================================================
// 02/11/2026 — Jetons d'API personnels (serveur MCP), écran « Mon compte »
// =============================================================================

export interface ApiTokenRow {
  id: string;
  name: string;
  prefix: string;
  /** Droits demandés à la création. Vide = tous ceux de la personne. */
  permissions: Permission[];
  /** Droits que le jeton porte réellement en ce moment. */
  effective: Permission[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  status: "active" | "expired" | "revoked";
}

export interface ApiTokensPayload {
  /** Droits qu'un nouveau jeton peut porter. */
  grantable: Permission[];
  /** Droits qu'aucun jeton ne porte jamais. */
  forbidden: Permission[];
  tokens: ApiTokenRow[];
}

export function listApiTokens() {
  return request<ApiTokensPayload>("/admin/me/api-tokens");
}

/** Fabrique un jeton ; `token` n'est renvoyé qu'ici, une seule fois. */
export function createApiTokenRequest(body: {
  name: string;
  expiresInDays?: number | null;
  permissions?: Permission[];
}) {
  return request<{
    token: string;
    id: string;
    name: string;
    prefix: string;
    permissions: Permission[];
    expiresAt: string | null;
    mcp: { apiUrl: string; envVar: string };
  }>("/admin/me/api-tokens", { method: "POST", body: JSON.stringify(body) });
}

export function revokeApiToken(id: string) {
  return request<ApiTokensPayload>(`/admin/me/api-tokens/${id}`, { method: "DELETE" });
}

/** Sprint Droits B2 : changement de son propre mot de passe (self-service). */
export function changePassword(currentPassword: string, newPassword: string) {
  return request<{ ok: true }>("/admin/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// =============================================================================
// Sprint W — Dashboard stats
// =============================================================================

export interface DashboardStats {
  pois: { total: number; active: number };
  parcours: { total: number; active: number };
  medias: { total: number };
  exhibitions: { total: number };
  collections: { total: number };
  pwaProjects: { live: number; draft: number };
}

export function getDashboardStats() {
  return request<DashboardStats>("/admin/stats");
}

/** SUPER_ADMIN : pose l'OUVERTURE DES VENTES d'une PWA (champ `statsStartAt`),
 *  borne du Reporting billetterie. `startAt` absent = maintenant, null = aucune. */
export function setTicketStatsStart(projectSlug: string, startAt?: string | null) {
  return request<{ ok: true; projectSlug: string; statsStartAt: string | null }>(
    "/admin/stats/tickets/start",
    { method: "PATCH", body: JSON.stringify({ projectSlug, startAt }) },
  );
}

// Sprint CU-1 : reset analytics counters. SUPER_ADMIN seulement. Scope
// determine par les headers globaux X-Tenant-Slug + X-Establishment-Slug.
// Confirmation explicite "DELETE" requise dans le body.
export interface ResetAnalyticsResult {
  ok: boolean;
  deleted: { analyticsEvents: number; visitSessions: number };
  scope: {
    tenantSlug: string;
    tenantName: string;
    establishmentSlug: string | null;
    parcoursSlug?: string | null;
  };
}
/**
 * Reset analytics (SUPER_ADMIN). Scope Client + Etablissement via headers
 * globaux ; scope Parcours optionnel via `parcoursSlug` (supprime les events
 * du parcours + de ses POIs etapes).
 */
export function resetAnalytics(parcoursSlug?: string) {
  return request<ResetAnalyticsResult>("/admin/analytics", {
    method: "DELETE",
    body: JSON.stringify({
      confirmation: "DELETE",
      ...(parcoursSlug ? { parcoursSlug } : {}),
    }),
  });
}

// =============================================================================
// Sprint X — Recherche globale
// =============================================================================

export interface GlobalSearchResults {
  pois: Array<{
    id: string;
    altoCode: string | null;
    audioguideCode: string | null;
    kind: "ARTWORK" | "SPACE" | "GAME" | "DIRECTION";
    title: string;
  }>;
  collections: Array<{ id: string; slug: string; title: string }>;
  exhibitions: Array<{
    id: string;
    slug: string;
    publicCode: string | null;
    title: string;
  }>;
  parcours: Array<{
    id: string;
    slug: string;
    profile: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY";
    title: string;
  }>;
  medias: Array<{
    id: string;
    filename: string;
    kind: "IMAGE" | "AUDIO" | "VIDEO" | "SUBTITLE";
  }>;
}

export function globalSearch(query: string) {
  return request<GlobalSearchResults>(
    `/admin/search?q=${encodeURIComponent(query)}`,
  );
}

// =============================================================================
// Users CRUD
// =============================================================================

export interface AdminUserListItem {
  id: string;
  email: string;
  displayName: string;
  role: AdminRole;
  active: boolean;
  lastLoginAt: string | null;
  failedLogins: number;
  /** Sprint Droits B1 : statut invitation. */
  invitedAt: string | null;
  inviteAcceptedAt: string | null;
  /** 02/11/2026 : compte Google rattaché, ou null. */
  googleEmail?: string | null;
  tenants: Array<{ slug: string; name: string; expiresAt: string | null }>;
  establishments: Array<{ id: string; slug: string; name: string; expiresAt: string | null }>;
  createdAt: string;
}

export function listUsers() {
  return request<AdminUserListItem[]>("/admin/users");
}

export interface CreateUserArgs {
  email: string;
  /** Sprint Droits B1 : optionnel → si absent, le compte est créé en mode
   * invitation (email avec lien magique pour définir le mot de passe). */
  password?: string;
  displayName?: string;
  role: AdminRole;
  tenantSlugs: string[];
  establishmentIds?: string[]; // Sprint O
  // Sprint Droits B3 : expiration optionnelle par adhésion (tenant: slug→date,
  // établissement: id→date "YYYY-MM-DD" ou null = illimité).
  tenantExpirations?: Record<string, string | null>;
  establishmentExpirations?: Record<string, string | null>;
}

export function createUser(args: CreateUserArgs) {
  return request<{
    id: string;
    email: string;
    role: AdminRole;
    invited?: boolean;
    invitePreviewUrl?: string;
  }>("/admin/users", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

/** Sprint Droits B1 : (re)envoie l'invitation à un utilisateur. */
export function inviteUser(id: string) {
  return request<PasswordResetResult>(`/admin/users/${id}/invite`, {
    method: "POST",
  });
}

export interface UpdateUserArgs {
  displayName?: string;
  role?: AdminRole;
  active?: boolean;
  tenantSlugs?: string[];
  establishmentIds?: string[]; // Sprint O
  password?: string;
  // Sprint Droits B3 : expiration optionnelle par adhésion.
  tenantExpirations?: Record<string, string | null>;
  establishmentExpirations?: Record<string, string | null>;
}

export function updateUser(id: string, args: UpdateUserArgs) {
  return request<{ id: string; email: string; role: AdminRole; active: boolean }>(
    `/admin/users/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify(args),
    },
  );
}

export function deleteUser(id: string) {
  return request<void>(`/admin/users/${id}`, { method: "DELETE" });
}

// Sprint Y-2 : reset password V1 (magic-link email)
export interface PasswordResetResult {
  ok: true;
  email: string;
  sent: boolean;
  /** En dev sans Resend, contient l'URL de reset pour test manuel. null en prod. */
  previewUrl: string | null;
}

export function resetUserPassword(id: string) {
  return request<PasswordResetResult>(`/admin/users/${id}/password-reset`, {
    method: "POST",
  });
}

// Self-service depuis l'ecran de login
export function forgotPassword(email: string) {
  return request<{ ok: true }>("/admin/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// Consomme le token, change le password
export function resetPasswordWithToken(args: { token: string; password: string }) {
  return request<{ ok: true }>("/admin/auth/reset", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

// =============================================================================
// Media upload (multipart)
// =============================================================================

export interface UploadedMedia {
  id: string;
  kind: "IMAGE" | "AUDIO" | "VIDEO" | "SUBTITLE";
  storageKey: string;
  bytes: number;
  width?: number | null;
  height?: number | null;
  optimized: boolean;
  variants?: object;
}

export async function uploadMedia(args: {
  file: File;
  tenantSlug?: string;
  establishmentSlug?: string;
  locale?: string;
  credit?: string;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<UploadedMedia> {
  const body = new FormData();
  body.append("file", args.file);
  if (args.tenantSlug) body.append("tenant", args.tenantSlug);
  if (args.establishmentSlug) body.append("establishment", args.establishmentSlug);
  if (args.locale) body.append("locale", args.locale);
  if (args.credit) body.append("credit", args.credit);

  // XHR pour avoir la progress upload (fetch ne l'expose pas)
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/admin/media`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    const tenant = args.tenantSlug ?? getCurrentTenant();
    if (tenant) xhr.setRequestHeader("X-Tenant-Slug", tenant);

    if (args.onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) args.onProgress!(e.loaded, e.total);
      });
    }
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadedMedia);
        } catch {
          reject(new ApiError("Invalid JSON response", xhr.status));
        }
      } else {
        if (xhr.status === 401) { clearToken(); redirectToLogin("expired"); }
        let msg = `${xhr.status} ${xhr.statusText}`;
        try {
          const body = JSON.parse(xhr.responseText) as ApiErrorPayload;
          if (body.message) msg = body.message;
        } catch {
          /* ignore */
        }
        reject(new ApiError(msg, xhr.status));
      }
    });
    xhr.addEventListener("error", () => reject(new ApiError("Network error", 0)));
    xhr.send(body);
  });
}

// =============================================================================
// Time codes assistes par IA (Gemini) — Sprint EB
// =============================================================================

export interface TimecodeRowDto {
  language: string;
  filename: string;
  refText: string;
  audioText: string;
  startMs: number;
  endMs: number;
}
export interface TimecodeResult {
  rows: TimecodeRowDto[];
  issues: Array<{ row: number; message: string }>;
  /** Reponse brute du modele (utile si 0 ligne parsee). */
  raw: string;
}

/**
 * Envoie un audio + des expressions-repere (1 par ligne) a l'API, qui interroge
 * Gemini et renvoie les time codes parses. Multipart (FormData) : on NE fixe PAS
 * Content-Type (le navigateur pose la boundary).
 */
export async function generateTimecodes(args: {
  file: File;
  language: string;
  expressions: string;
}): Promise<TimecodeResult> {
  const body = new FormData();
  // Champs AVANT le fichier : avec @fastify/multipart, les champs placés après
  // le fichier ne sont peuplés qu'une fois le flux du fichier consommé côté
  // serveur (l'endpoint draine le fichier d'abord, mais on reste robuste ici).
  body.append("language", args.language);
  body.append("expressions", args.expressions);
  body.append("file", args.file);

  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const tenant = getCurrentTenant();
  if (tenant) headers.set("X-Tenant-Slug", tenant);

  const res = await fetch(`${API_BASE}/admin/timecode`, { method: "POST", headers, body });
  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      redirectToLogin("expired");
    }
    let msg = `${res.status} ${res.statusText}`;
    try {
      const b = (await res.json()) as ApiErrorPayload;
      if (b.message) msg = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as TimecodeResult;
}

/**
 * Étape EB (éditeur de diaporama) : time codes depuis un audio DÉJÀ uploadé
 * (Media.id, lu côté serveur depuis S3). Pas de re-upload de la narration.
 */
export async function generateTimecodesFromMedia(args: {
  mediaId: string;
  language: string;
  expressions: string[];
}): Promise<TimecodeResult> {
  return request<TimecodeResult>("/admin/timecode/from-media", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface AdminTenant {
  id: string;
  slug: string;
  name: string;
  shortCode: string;
  active: boolean;
  logoMediaId: string | null;
  logoUrl: string | null;
  exhibitions: number;
  /** Sprint AQ-5 : DISTINCT(POI direct UNION POI via Etablissements rattaches) */
  pois: number;
  /** Sprint AQ-5 : POI rattaches directement (POI.tenantId = this.id) */
  poisDirect: number;
  /** Sprint AQ-5 : POI rattaches via un Etablissement de ce Tenant */
  poisViaEstablishments: number;
  media: number;
  tickets: number;
  createdAt: string;
}

export function listTenants() {
  return request<AdminTenant[]>("/admin/tenants");
}

// =============================================================================
// Exhibitions
// =============================================================================

export interface AdminExhibition {
  id: string;
  slug: string;
  type: "PERMANENT" | "TEMPORARY";
  publicCode: string | null;
  internalCode: string | null;
  startDate: string | null;
  endDate: string | null;
  active: boolean;
  // Lot 4 : fenêtre de validité d'accès au contenu
  accessWindowEnabled: boolean;
  accessWindowBasis: AccessWindowBasis | null;
  accessDaysBefore: number | null;
  accessDaysAfter: number | null;
  notes: string | null;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  coverMediaId: string | null;
  coverUrl: string | null;
  title: string;
  titleEn: string | null;
  descriptionFr: string | null;
  descriptionEn: string | null;
  parcoursCount: number;
  createdAt: string;
}

export function listExhibitions() {
  return request<AdminExhibition[]>("/admin/exhibitions");
}

/** Expos d'un tenant explicite (override du header X-Tenant-Slug). Utilisé par
 *  le simulateur, qui peut viser un tenant autre que le tenant courant. */
export function listExhibitionsForTenant(tenantSlug: string) {
  return request<AdminExhibition[]>("/admin/exhibitions", {}, { tenantSlug });
}

// =============================================================================
// Parcours
// =============================================================================

export interface AdminParcours {
  id: string;
  slug: string;
  // Sprint CX-4 : profil CUSTOM
  profile: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY" | "CUSTOM";
  customProfileName: string | null;
  active: boolean;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  exhibition: { slug: string; type: "PERMANENT" | "TEMPORARY" } | null;
  collection: { slug: string; isArchive: boolean } | null;
  title: string;
  stepsCount: number;
  createdAt: string;
}

export function listParcours() {
  return request<AdminParcours[]>("/admin/parcours");
}

export function listParcoursForTenant(tenantSlug: string) {
  return request<AdminParcours[]>("/admin/parcours", {}, { tenantSlug });
}

// =============================================================================
// POIs
// =============================================================================

// Sprint BF-1 : "INTRO" pour les POIs d'introduction de parcours
// (toujours en sortOrder=0 du parcours, affiches comme entete dans la PWA)
export type POIKind = "ARTWORK" | "SPACE" | "GAME" | "INTRO" | "DIRECTION";

export interface AdminPOIListItem {
  id: string;
  altoCode: string | null;
  kind: POIKind;
  audioguideCode: string | null;
  internalCode: string | null;
  tmsId: string | null;
  tmsObjectIds: string[];
  tmsZoneId: string | null;
  tmsSyncedAt?: string | null;
  tmsPinned: boolean;
  museumRoom: string | null;
  /** Sprint DQ : Salle structurée rattachée (rattachement via roomId). */
  roomId: string | null;
  room: { id: string; label: string; level: string; title: string } | null;
  /** Sprint AX-3 : Lieu d'origine (oeuvres architecturales) */
  location: string | null;
  active: boolean;
  tenant: { slug: string; shortCode: string };
  establishment: { id: string; slug: string; name: string; shortCode: string | null } | null;
  title: string;
  author: string;
  mediaCount: number;
  stepsCount: number;
  createdAt: string;
}

export interface ListPOIsParams {
  tenant?: string;
  parcours?: string;
  collection?: string;
  exhibition?: string;
  establishment?: string;
  kind?: POIKind;
  q?: string;
  limit?: number;
}

export function listPOIs(params: ListPOIsParams = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") qs.set(k, String(v));
  }
  const q = qs.toString();
  return request<AdminPOIListItem[]>(`/admin/pois${q ? "?" + q : ""}`);
}

export type TranslationField =
  | "TITLE" | "SUBTITLE" | "DESCRIPTION" | "TRANSCRIPT" | "CARTEL"
  | "AUTHOR" | "AUTHOR_DATES" | "ALT" | "CAPTION" | "CREDIT" | "INTRO" | "AUDIENCE"
  // Sprint BA-2 : Lieu (oeuvres architecturales) — traduisible
  | "LOCATION"
  // Retour Olivier 21/09 : annee de creation traduisible (« vers 1800 »)
  | "ARTWORK_YEAR";

export interface AdminTranslation {
  id: string;
  field: TranslationField;
  localeCode: string;
  value: string;
}

export type POIMediaRole =
  | "MAIN_IMAGE" | "FRAMED_IMAGE" | "GALLERY_IMAGE"
  | "MAIN_AUDIO" | "KIDS_AUDIO" | "LSF_VIDEO" | "TRANSCRIPT" | "VIDEO";

export interface AdminMediaRef {
  id: string;
  kind: "IMAGE" | "AUDIO" | "VIDEO" | "SUBTITLE";
  storageKey: string;
  url: string;
  mimeType: string;
  bytes: number;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  localeCode: string | null;
  defaultCredit: string | null;
  originalFilename: string | null;
  /** Sprint DS : alt localise (Translation MEDIA/ALT) — present sur le
   *  detail POI uniquement, pour pre-remplir l'editeur de diaporama. */
  altByLocale?: Record<string, string>;
}

export interface AdminPOIAttachment {
  id: string;
  role: POIMediaRole;
  sortOrder: number;
  credit: string | null;
  /**
   * 04/11/2026 : phrase sous l'image DANS ce POI, une par langue (Translation
   * POI_MEDIA/CAPTION). Absente d'une API plus ancienne.
   */
  captions?: Array<{ id: string; localeCode: string; value: string }>;
  media: AdminMediaRef;
}

export interface AdminPOIDetail {
  id: string;
  kind: POIKind;
  audioguideCode: string | null;
  internalCode: string | null;
  tmsId: string | null;
  /** 28/10/2026 : un arret d'audioguide couvre parfois plusieurs objets du TMS. */
  tmsObjectIds: string[];
  /** Zone resolue par la derniere synchronisation ; null = hors salle. */
  tmsZoneId: string | null;
  tmsSyncedAt: string | null;
  /** « Ne pas synchroniser » : la saisie manuelle fait foi. */
  tmsPinned: boolean;
  museumLevel: string | null;
  museumRoom: string | null;
  /** Sprint DQ : rattachement structuré à une Salle (Room). */
  roomId: string | null;
  /** 05/11/2026 : POI de salle, zones du TMS dont il montre les œuvres. */
  coveredZoneIds?: string[];
  artworkYear: string | null;
  artworkTechnique: string | null;
  artworkDimensions: string | null;
  artworkInventoryNumber: string | null;
  active: boolean;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  translations: AdminTranslation[];
  attachments: AdminPOIAttachment[];
  steps: {
    parcoursId: string;
    parcoursSlug: string;
    parcoursProfile: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY";
    sortOrder: number;
  }[];
  // Sprint CJ-1 : rattachements M-N a Collection / Exposition
  collectionLinks: {
    id: string;
    collectionId: string;
    collectionSlug: string;
    collectionTitle: string;
    isArchive: boolean;
    addedAt: string;
  }[];
  exhibitionLinks: {
    id: string;
    exhibitionId: string;
    exhibitionSlug: string;
    exhibitionTitle: string;
    exhibitionType: "PERMANENT" | "TEMPORARY";
    startDate: string | null;
    endDate: string | null;
    addedAt: string;
  }[];
  // Sprint DS : diaporama synchronise a l'audio (meme forme que le payload
  // du PUT /admin/pois/:id/slideshow — lire, editer, renvoyer tel quel).
  slideshow: AdminPOISlideshow;
  createdAt: string;
  updatedAt: string;
}

// Sprint DS : diaporama d'images synchronise a l'audio (epopee #8).
// Toutes les references sont des POIMedia.id (attachments du POI) : images
// membres = attachments *_IMAGE, pistes = attachments MAIN_AUDIO/KIDS_AUDIO/
// LSF_VIDEO. Le PUT remplace l'etat complet du diaporama.
export interface AdminPOISlideshowCue {
  poiMediaId: string;
  atMs: number;
  /** Aide d'edition (mot prononce repere a l'ecoute) — optionnelle. */
  word: string | null;
}

export interface AdminPOISlideshowTrack {
  audioPoiMediaId: string;
  cues: AdminPOISlideshowCue[];
}

export interface AdminPOISlideshow {
  enabled: boolean;
  fadeMs: number;
  coverPoiMediaId: string | null;
  /** Images membres ORDONNEES (max 10). */
  images: { poiMediaId: string }[];
  tracks: AdminPOISlideshowTrack[];
}

export function getPOI(id: string) {
  return request<AdminPOIDetail>(`/admin/pois/${id}`);
}

/** Sprint DS : remplace le diaporama synchronise du POI (payload complet). */
export function updatePOISlideshow(poiId: string, payload: AdminPOISlideshow) {
  return request<{ ok: true; warnings: string[] }>(`/admin/pois/${poiId}/slideshow`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// Sprint CJ-1 : rattachement M-N POI <-> Collection / Exposition
export function attachPoiToCollection(poiId: string, collectionId: string) {
  return request<{ id: string }>(`/admin/pois/${poiId}/collections`, {
    method: "POST",
    body: JSON.stringify({ collectionId }),
  });
}

export function detachPoiFromCollection(poiId: string, linkId: string) {
  return request<void>(`/admin/pois/${poiId}/collections/${linkId}`, {
    method: "DELETE",
  });
}

export function attachPoiToExhibition(poiId: string, exhibitionId: string) {
  return request<{ id: string }>(`/admin/pois/${poiId}/exhibitions`, {
    method: "POST",
    body: JSON.stringify({ exhibitionId }),
  });
}

export function detachPoiFromExhibition(poiId: string, linkId: string) {
  return request<void>(`/admin/pois/${poiId}/exhibitions/${linkId}`, {
    method: "DELETE",
  });
}

// =============================================================================
// Media
// =============================================================================

// Sprint Z-3 : variants generes par imageOptim.ts (9 = 3 formats × 3 tailles)
export interface MediaVariantInfo {
  key: string; // storage key dans S3
  width: number;
  height: number;
  bytes: number;
}

export interface MediaVariants {
  original: MediaVariantInfo;
  avif: { thumb: MediaVariantInfo; medium: MediaVariantInfo; full: MediaVariantInfo };
  webp: { thumb: MediaVariantInfo; medium: MediaVariantInfo; full: MediaVariantInfo };
  jpeg: { thumb: MediaVariantInfo; medium: MediaVariantInfo; full: MediaVariantInfo };
}

export interface AdminMediaListItem {
  id: string;
  kind: "IMAGE" | "AUDIO" | "VIDEO" | "SUBTITLE";
  storageKey: string;
  url: string;
  mimeType: string;
  bytes: number;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  localeCode: string | null;
  defaultCredit: string | null;
  originalFilename: string | null;
  // Sprint S : tenant nullable (media globaux)
  tenant: { slug: string; shortCode: string } | null;
  establishment: { id: string; slug: string; name: string } | null;
  usedBy: number;
  // Sprint Z-3 : variants generes par le pipeline Sharp (uniquement images)
  hasVariants: boolean;
  variants: MediaVariants | null;
  createdAt: string;
}

export interface UpdateMediaArgs {
  tenantSlug?: string | null;
  establishmentId?: string | null;
  localeCode?: string | null;
  defaultCredit?: string | null;
}

export function updateMedia(id: string, args: UpdateMediaArgs) {
  return request<{ id: string }>(`/admin/media/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteMedia(id: string) {
  return request<void>(`/admin/media/${id}`, { method: "DELETE" });
}

// Sprint CO-1 : bulk-delete N media non-utilises en une seule operation.
// Le backend skip silencieusement les media en usage et les media hors-tenant ;
// le resultat detaille permet d'afficher un toast precis ("3 supprimes, 2 en
// usage, 1 hors-tenant").
export interface BulkDeleteMediaResult {
  ok: boolean;
  deleted: number;
  inUse: Array<{ id: string; filename: string | null; reason: string }>;
  forbidden: Array<{ id: string; filename: string | null }>;
  notFound: number;
}
export function bulkDeleteMedia(ids: string[]) {
  return request<BulkDeleteMediaResult>("/admin/media/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export interface MediaUsage {
  pois: Array<{
    id: string;
    altoCode: string | null;
    audioguideCode: string | null;
    title: string | null;
    role: string;
    /** Recette 2026-06-11 : contexte du POI (slugs) pour situer le media. */
    parcours: string[];
    collections: string[];
    exhibitions: string[];
  }>;
  collections: Array<{ id: string; slug: string }>;
  exhibitions: Array<{ id: string; slug: string }>;
  parcours: Array<{ id: string; slug: string }>;
  miniGames: Array<{ id: string; slug: string }>;
  pwaProjects: Array<{ id: string; slug: string; displayName: string }>;
  establishments: Array<{ id: string; slug: string; name: string }>;
  tenants: Array<{ id: string; slug: string; name: string }>;
}

export function getMediaUsage(id: string) {
  return request<MediaUsage>(`/admin/media/${id}/usage`);
}

export function listMedia(
  params: {
    tenant?: string;
    kind?: AdminMediaListItem["kind"];
    locale?: string;
    // Sprint AJ : recherche libre sur originalFilename (insensible casse)
    q?: string;
    // Sprint BE-2 : filtre Parcours (slug ou id) — remplace les anciens
    // filtres collection+exhibition AW-1 qui n'avaient pas de sens.
    parcours?: string;
    limit?: number;
  } = {},
) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") qs.set(k, String(v));
  }
  const queryStr = qs.toString();
  return request<AdminMediaListItem[]>(`/admin/media${queryStr ? "?" + queryStr : ""}`);
}

// =============================================================================
// Audit
// =============================================================================

export interface AdminAuditEntry {
  id: string;
  action: string;
  actor: string | null;
  subject: string | null;
  details: unknown;
  tenantSlug: string | null;
  createdAt: string;
  /** Partenaire d'origine, deduit cote serveur du prefixe du code ou de
   *  l'acteur. null = indeterminable (ni code, ni acteur parlant). */
  partner?: { slug: string; name: string } | null;
  /** Etat REEL du billet porte par l'entree (lu en base, pas deduit du journal).
   *  null si l'entree ne porte aucun code-barres connu. */
  ticketState?: {
    /** ISSUED | ACTIVATED | CANCELLED, ou INACTIVE si le code est desactive. */
    status: string;
    /** Le billet a-t-il ete active au moins une fois ? */
    activated: boolean;
    useCount: number;
    /** Libelle de l'appareil lie (usage unique), si connu. */
    device: string | null;
  } | null;
}

export function listAudit(limit = 100, days?: number) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (days) qs.set("days", String(days));
  return request<AdminAuditEntry[]>(`/admin/audit?${qs.toString()}`);
}

// Sprint ED-3 : diagnostic See Tickets (test live + SAV comptoir).
export interface SeeTicketsBarcodeDiagnostic {
  found: boolean;
  value: string;
  verdict: "OK" | "REFUSE";
  reason: string;
  museum?: { slug: string; name: string } | null;
  tarif?: string;
  /** Exposition ouverte par le billet (null = tout le musée). */
  exhibitionSlug?: string | null;
  productCode?: string;
  ticketExternalId?: string;
  ticketStatus?: string;
  showName?: string | null;
  showCancelled?: boolean;
  barcodeActive?: boolean;
  sessionsOpened?: number;
  singleUseExempt?: boolean;
  boundToDevice?: boolean;
  /** Liaison posee depuis une PREVIEW (prod-*) : cloisonnee du site public. */
  boundToDevicePreview?: boolean;
  boundDevicePreview?: {
    label: string | null;
    at: string | null;
    fingerprint: string;
  } | null;
  /** Detail de la liaison appareil (null si jamais lie, ou lie avant l'ajout). */
  boundDevice?: {
    /** « iPhone · Safari », ou null si le User-Agent etait inexploitable. */
    label: string | null;
    /** Date ISO de la liaison, ou null. */
    at: string | null;
    /** 8 premiers caracteres de l'empreinte : sert a COMPARER deux appareils. */
    fingerprint: string;
  } | null;
  receivedAt?: string;
  sourceUpdatedAt?: string | null;
  /** V1.50 — renouvellement de l'accès : nombre déjà fait pour ce billet. */
  renewals?: number;
  /** Renouvellements possibles au comptoir ; au-delà, un administrateur valide. */
  renewalLimit?: number;
  /** Ce code a été remplacé par un renouvellement (date ISO), sinon null. */
  replacedAt?: string | null;
  /** Code en cours du billet, s'il diffère du code cherché. */
  currentCode?: string | null;
  /** Le bouton « Renouveler l'accès » a-t-il un sens pour ce code ? */
  renewable?: boolean;
}

/** V1.50 — résultat d'un renouvellement d'accès (POST /admin/seetickets/renew). */
export interface RenewAccessResult {
  ok: boolean;
  /** Nouveau code, 14 chiffres. */
  value: string;
  /** Lien d'ouverture (QR), null si l'app du musée est introuvable. */
  url: string | null;
  renewals: number;
  previousDevice: string | null;
}

export function renewAccess(value: string, reason?: string) {
  return request<RenewAccessResult>("/admin/seetickets/renew", {
    method: "POST",
    body: JSON.stringify({ value, ...(reason ? { reason } : {}) }),
  });
}

export interface SeeTicketsRecentTicket {
  externalId: string;
  museum: string | null;
  tarif: string;
  status: string;
  showName: string | null;
  showCancelled: boolean;
  barcodesTotal: number;
  barcodesActive: number;
  receivedAt: string;
}

export function diagnoseSeeTicketsBarcode(value: string) {
  return request<SeeTicketsBarcodeDiagnostic>(
    `/admin/seetickets/barcode?value=${encodeURIComponent(value)}`,
  );
}

export function listRecentSeeTickets(limit = 30) {
  return request<SeeTicketsRecentTicket[]>(`/admin/seetickets/recent?limit=${limit}`);
}

// --- Codes de test (usage unique exempte) ---------------------------------
export interface TestCode {
  id: string;
  value: string | null;
  establishment: { slug: string; name: string } | null;
  sessions: number;
  /** Cle revoquee (le code n'ouvre plus rien). */
  revoked: boolean;
  /** Code-barres encore actif. Faux = desactive, comme `revoked`. */
  barcodeActive: boolean;
  createdAt: string;
}
export interface TestCodesResponse {
  codes: TestCode[];
  establishments: { id: string; slug: string; name: string }[];
}
export function listTestCodes() {
  return request<TestCodesResponse>("/admin/seetickets/test-codes");
}
export function createTestCode(establishmentId: string) {
  return request<{ value: string; establishment: { slug: string; name: string } }>(
    "/admin/seetickets/test-codes",
    { method: "POST", body: JSON.stringify({ establishmentId }) },
  );
}
export function deleteTestCode(id: string) {
  return request<{ ok: boolean }>(
    `/admin/seetickets/test-codes/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}
export function setCodeExempt(value: string, exempt: boolean) {
  return request<{ ok: boolean; updated: number; exempt: boolean }>(
    "/admin/seetickets/exempt",
    { method: "PATCH", body: JSON.stringify({ value, exempt }) },
  );
}

// =============================================================================
// Translations PATCH (upsert)
// =============================================================================

export interface UpsertTranslationArgs {
  id?: string;
  entity?: "EXHIBITION" | "PARCOURS" | "POI" | "MEDIA" | "POI_MEDIA";
  field?: TranslationField;
  localeCode?: string;
  poiId?: string;
  exhibitionId?: string;
  parcoursId?: string;
  mediaId?: string;
  /** 04/11/2026 : legende d'une image dans un POI (entity POI_MEDIA). */
  poiMediaId?: string;
  value: string;
}

export function upsertTranslation(args: UpsertTranslationArgs) {
  return request<AdminTranslation & { entity: string }>("/admin/translations", {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

// =============================================================================
// Sprint D-1 — Tenants CRUD
// =============================================================================

export interface CreateTenantArgs {
  slug: string;
  name: string;
  shortCode: string;
  config?: Record<string, unknown>;
}
export function createTenant(args: CreateTenantArgs) {
  return request<{ id: string; slug: string }>("/admin/tenants", {
    method: "POST",
    body: JSON.stringify(args),
  });
}
export interface UpdateTenantArgs {
  name?: string;
  shortCode?: string;
  config?: Record<string, unknown>;
  active?: boolean;
  logoMediaId?: string | null;
}
export function updateTenant(id: string, args: UpdateTenantArgs) {
  return request<{ id: string; slug: string; name: string; active: boolean }>(
    `/admin/tenants/${id}`,
    { method: "PATCH", body: JSON.stringify(args) },
  );
}
export function deleteTenant(id: string) {
  return request<void>(`/admin/tenants/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sprint D-1 — Exhibitions CRUD
// =============================================================================

export interface CreateExhibitionArgs {
  tenantSlug: string;
  // Sprint AO-2 : rattachement Etablissement (optionnel)
  establishmentId?: string;
  slug: string;
  type: "PERMANENT" | "TEMPORARY";
  publicCode?: string;
  internalCode?: string;
  startDate?: string; // ISO
  endDate?: string;
  titleFr: string;
  titleEn?: string;
  descriptionFr?: string;
  descriptionEn?: string;
  // Lot 4 : fenêtre de validité d'accès au contenu
  accessWindowEnabled?: boolean;
  accessWindowBasis?: AccessWindowBasis;
  accessDaysBefore?: number | null;
  accessDaysAfter?: number | null;
}
export function createExhibition(args: CreateExhibitionArgs) {
  return request<{ id: string; slug: string }>("/admin/exhibitions", {
    method: "POST",
    body: JSON.stringify(args),
  });
}
export interface UpdateExhibitionArgs {
  // Sprint AM : permet de corriger Client + Etab si Exposition vide
  tenantSlug?: string;
  establishmentId?: string | null;
  type?: "PERMANENT" | "TEMPORARY";
  publicCode?: string | null;
  internalCode?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  active?: boolean;
  notes?: string | null;
  coverMediaId?: string | null;
  titleFr?: string;
  titleEn?: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  // Lot 4 : fenêtre de validité d'accès au contenu
  accessWindowEnabled?: boolean;
  accessWindowBasis?: AccessWindowBasis;
  accessDaysBefore?: number | null;
  accessDaysAfter?: number | null;
}
export function updateExhibition(id: string, args: UpdateExhibitionArgs) {
  return request<{ id: string; slug: string }>(`/admin/exhibitions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}
export function deleteExhibition(id: string) {
  return request<void>(`/admin/exhibitions/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sprint D-1 — Parcours CRUD + steps
// =============================================================================

export interface CreateParcoursArgs {
  tenantSlug: string;
  /** Rattachement Exposition OR Collection (Sprint M : on supporte les 2). */
  exhibitionId?: string;
  collectionId?: string;
  /** Sprint R : image de couverture. */
  coverMediaId?: string;
  slug: string;
  // Sprint CX-4 : profil CUSTOM avec customProfileName libre
  profile: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY" | "CUSTOM";
  customProfileName?: string;
  estimatedMinutes?: number;
  titleFr: string;
  titleEn?: string;
}
export function createParcours(args: CreateParcoursArgs) {
  return request<{ id: string; slug: string }>("/admin/parcours", {
    method: "POST",
    body: JSON.stringify(args),
  });
}
export interface UpdateParcoursArgs {
  exhibitionId?: string | null;
  collectionId?: string | null;
  coverMediaId?: string | null;
  // Sprint CX-4 : profil CUSTOM
  profile?: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY" | "CUSTOM";
  customProfileName?: string | null;
  estimatedMinutes?: number | null;
  titleFr?: string;
  titleEn?: string | null;
  active?: boolean;
  sortOrder?: number;
}
export function updateParcours(id: string, args: UpdateParcoursArgs) {
  return request<{ id: string; slug: string }>(`/admin/parcours/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}
export function deleteParcours(id: string) {
  return request<void>(`/admin/parcours/${id}`, { method: "DELETE" });
}

export interface AdminParcoursStep {
  id: string;
  sortOrder: number;
  poi: {
    id: string;
    audioguideCode: string | null;
    kind: POIKind;
    // Sprint BO-2 : expose Salle/Niveau pour orienter l'admin dans la
    // construction du parcours.
    museumRoom: string | null;
    museumLevel: string | null;
    title: string;
    author: string;
  };
}

export interface AdminParcoursDetail {
  id: string;
  slug: string;
  // Sprint CX-4 : profil CUSTOM avec customProfileName libre
  profile: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY" | "CUSTOM";
  customProfileName: string | null;
  active: boolean;
  estimatedMinutes: number | null;
  sortOrder: number;
  tenant: { slug: string; name: string; shortCode: string };
  coverMediaId: string | null;
  coverUrl: string | null;
  establishment: { id: string; slug: string; name: string } | null;
  exhibition: { id: string; slug: string; type: "PERMANENT" | "TEMPORARY" } | null;
  collection: { id: string; slug: string; isArchive: boolean } | null;
  translations: AdminTranslation[];
  steps: AdminParcoursStep[];
}

export function getParcours(id: string) {
  return request<AdminParcoursDetail>(`/admin/parcours/${id}`);
}

export function addParcoursStep(parcoursId: string, args: { poiId: string; sortOrder?: number }) {
  return request<{ id: string; sortOrder: number }>(
    `/admin/parcours/${parcoursId}/steps`,
    { method: "POST", body: JSON.stringify(args) },
  );
}
export function removeParcoursStep(parcoursId: string, stepId: string) {
  return request<void>(`/admin/parcours/${parcoursId}/steps/${stepId}`, { method: "DELETE" });
}
export function reorderParcoursSteps(parcoursId: string, stepIds: string[]) {
  return request<void>(`/admin/parcours/${parcoursId}/reorder`, {
    method: "PATCH",
    body: JSON.stringify({ stepIds }),
  });
}

// =============================================================================
// Sprint D-1 — POI CRUD + attach media
// =============================================================================

export interface CreatePOIArgs {
  tenantSlug: string;
  establishmentId?: string;
  kind: POIKind;
  audioguideCode?: string;
  internalCode?: string;
  tmsId?: string;
  museumLevel?: string;
  museumRoom?: string;
  artworkYear?: string;
  artworkTechnique?: string;
  artworkDimensions?: string;
  artworkInventoryNumber?: string;
  titleFr: string;
  authorFr?: string;
}
export function createPOI(args: CreatePOIArgs) {
  return request<{ id: string; audioguideCode: string | null }>("/admin/pois", {
    method: "POST",
    body: JSON.stringify(args),
  });
}
export interface UpdatePOIArgs {
  // Sprint AW-3 : rattachement Etablissement (deja accepte cote backend
  // via POIUpdateSchema.establishmentId, juste pas expose cote admin lib)
  establishmentId?: string | null;
  // Sprint BG-1 : permet de changer le kind (ARTWORK -> INTRO, etc.)
  kind?: POIKind;
  audioguideCode?: string | null;
  internalCode?: string | null;
  tmsId?: string | null;
  tmsObjectIds?: string[];
  tmsPinned?: boolean;
  museumLevel?: string | null;
  museumRoom?: string | null;
  /** Sprint DQ : rattachement structuré à une Salle (Room). null = détacher. */
  roomId?: string | null;
  /** 05/11/2026 : POI de salle, zones du TMS dont il montre les œuvres. */
  coveredZoneIds?: string[];
  /** Sprint AX-3 : Lieu (oeuvres architecturales) */
  location?: string | null;
  artworkYear?: string | null;
  artworkTechnique?: string | null;
  artworkDimensions?: string | null;
  artworkInventoryNumber?: string | null;
  active?: boolean;
}
export function updatePOI(id: string, args: UpdatePOIArgs) {
  return request<{ id: string }>(`/admin/pois/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}
export function deletePOI(id: string) {
  return request<void>(`/admin/pois/${id}`, { method: "DELETE" });
}

export function attachMediaToPOI(
  poiId: string,
  args: { mediaId: string; role: POIMediaRole; sortOrder?: number; credit?: string },
) {
  return request<{ id: string }>(`/admin/pois/${poiId}/attachments`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}
export function detachMediaFromPOI(poiId: string, attachmentId: string) {
  return request<void>(`/admin/pois/${poiId}/attachments/${attachmentId}`, {
    method: "DELETE",
  });
}
/** Sprint DO-2 : réordonne les médias d'un POI (drag & drop). `orderedIds` =
 * liste ordonnée des ids d'attachements ; le backend réécrit le sortOrder. */
export function reorderPOIMedia(poiId: string, orderedIds: string[]) {
  return request<void>(`/admin/pois/${poiId}/attachments/reorder`, {
    method: "PATCH",
    body: JSON.stringify({ orderedIds }),
  });
}

// =============================================================================
// Sprint V — Import CSV standard ALTO + Wizard bulk POI
// =============================================================================

export interface CsvDryRunResult {
  totalRows: number;
  validRows: number;
  skippedRows: number;
  warnings: string[];
  errors: string[];
  previewRows: Array<{
    rowNum: number;
    audioguideCode: string | null;
    kind: POIKind;
    museumRoom: string | null;
    collectionSlug: string | null;
    exhibitionSlug: string | null;
    parcoursSlug: string | null;
    translations: Array<{ field: string; localeCode: string; value: string }>;
  }>;
  // Sprint BD-1 : detection des collisions audioguide_code+kind dans le tenant.
  // Renvoye uniquement si le wizard a passe tenant + (optionnel) establishment
  // au dry-run. Vide sinon (back-compat).
  collisionsSameEstablishment?: Array<{
    audioguideCode: string;
    kind: POIKind;
    existingTitle: string | null;
  }>;
  collisionsOtherEstablishment?: Array<{
    audioguideCode: string;
    kind: POIKind;
    existingTitle: string | null;
    existingEstablishment: { slug: string; name: string } | null;
  }>;
}

export interface CsvCommitResult {
  parsed: {
    totalRows: number;
    validRows: number;
    skippedRows: number;
    warnings: string[];
  };
  committed: {
    poisCreated: number;
    poisUpdated: number;
    translationsUpserted: number;
    collectionLinksCreated: number;
    exhibitionLinksCreated: number;
    parcoursStepsCreated: number;
    /** Sprint AL : images effectivement telechargees + attachees aux POI. */
    imagesAttached?: number;
    /** Sprint EA : medias deja uploades rattaches par nom de fichier (image + audio). */
    mediaLinked?: number;
    errors: Array<{ rowNum: number; message: string }>;
  };
}

async function postMultipart<T>(path: string, form: FormData): Promise<T> {
  const headers = new Headers();
  headers.set("Accept", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const tenant = getCurrentTenant();
  if (tenant) headers.set("X-Tenant-Slug", tenant);
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: form });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as ApiErrorPayload;
      if (body.message) msg = body.message;
    } catch {
      /* ignore */
    }
    if (res.status === 401) {
      clearToken();
      redirectToLogin("expired");
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

// Sprint ED-2 (charte) : upload d'une police WOFF2 custom pour un PwaProject.
// Renvoie la storageKey S3 (a poser dans customization.branding.customFontKey).
export function uploadProjectFont(
  projectId: string,
  file: File,
): Promise<{ storageKey: string; bytes: number }> {
  const fd = new FormData();
  fd.append("file", file);
  return postMultipart(`/admin/pwa-projects/${projectId}/font`, fd);
}

// Sprint BD-1 : passe tenant + establishment optionnels pour activer
// la detection de collision audioguide_code dans le dry-run.
export function importCsvDryRun(
  file: File,
  opts?: { tenantSlug?: string; establishmentSlug?: string },
): Promise<CsvDryRunResult> {
  const fd = new FormData();
  // IMPORTANT : les champs texte AVANT le fichier. Cote serveur, req.file()
  // lit data.fields avant de consumer le flux du fichier -> les champs places
  // apres le fichier ne sont pas encore parses (tenant/establishment perdus).
  if (opts?.tenantSlug) fd.append("tenant", opts.tenantSlug);
  if (opts?.establishmentSlug) fd.append("establishment", opts.establishmentSlug);
  fd.append("file", file);
  return postMultipart<CsvDryRunResult>("/admin/import/csv/dry-run", fd);
}

/**
 * Modèle CSV ALTO (colonnes standard + 2 lignes d'exemple), servi par l'API
 * (GET /admin/import/csv-template). Téléchargé avec la session : un simple lien
 * n'emporterait pas le jeton, et l'API refuse sans lui.
 */
export async function downloadCsvTemplate(): Promise<void> {
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const tenant = getCurrentTenant();
  if (tenant) headers.set("X-Tenant-Slug", tenant);
  const res = await fetch(`${API_BASE}/admin/import/csv-template`, { headers });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = "alto-import-modele.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function importCsvCommit(args: {
  file: File;
  tenantSlug: string;
  establishmentSlug?: string;
  mode?: "create-only" | "upsert";
}): Promise<CsvCommitResult> {
  const fd = new FormData();
  // IMPORTANT : champs texte AVANT le fichier (cf. importCsvDryRun) — sinon
  // req.file() cote serveur ne voit pas tenant/establishment/mode et renvoie
  // "Champ 'tenant' manquant".
  fd.append("tenant", args.tenantSlug);
  if (args.establishmentSlug) fd.append("establishment", args.establishmentSlug);
  fd.append("mode", args.mode ?? "upsert");
  fd.append("file", args.file);
  return postMultipart<CsvCommitResult>("/admin/import/csv/commit", fd);
}


export interface BulkPOIItem {
  audioguideCode?: string;
  kind: POIKind;
  museumRoom?: string;
  titleFr: string;
  titleEn?: string;
  authorFr?: string;
  artworkYear?: string;
}

export interface BulkPOIArgs {
  tenantSlug: string;
  establishmentId?: string;
  collectionSlug?: string;
  exhibitionSlug?: string;
  parcoursSlug?: string;
  items: BulkPOIItem[];
}

export interface BulkPOIResult {
  created: Array<{ id: string; audioguideCode: string | null }>;
  errors: Array<{ index: number; message: string }>;
}

export function bulkCreatePOIs(args: BulkPOIArgs) {
  return request<BulkPOIResult>("/admin/pois/bulk", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

// Sprint X-3 : bulk publish/unpublish/delete
export interface BulkUpdatePOIsResult {
  ok: boolean;
  affected: number;
  skipped: number;
}

export function bulkUpdatePOIs(args: {
  ids: string[];
  action: "publish" | "unpublish" | "delete";
}) {
  return request<BulkUpdatePOIsResult>("/admin/pois/bulk-update", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

// Sprint CK-1 : bulk-attach N POI a une Collection ou Exposition (M-N).
// Idempotent (skip les POI deja rattaches). targetKind dicte la table de
// jointure cote backend.
export interface BulkAttachPOIsResult {
  ok: boolean;
  attached: number;       // nb de liens créés
  alreadyLinked: number;  // nb de POI deja rattaches (skip)
  skipped: number;        // nb de POI hors-tenant (skip)
}
export function bulkAttachPOIsToTarget(args: {
  ids: string[];
  targetKind: "COLLECTION" | "EXHIBITION";
  targetId: string;
}) {
  return request<BulkAttachPOIsResult>("/admin/pois/bulk-attach", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

// Sprint X-4 : duplicate POI
export function duplicatePOI(id: string) {
  return request<{ id: string; altoCode: string | null }>(
    `/admin/pois/${id}/duplicate`,
    { method: "POST" },
  );
}

// =============================================================================
// Sprint D-3 — SAV : achats manuels
// =============================================================================

export type TicketSource = "SEETICKETS" | "VENTRATA" | "BOKUN" | "REZDY" | "MANUAL" | "OCTO";
export type TicketStatus = "ISSUED" | "ACTIVATED" | "EXPIRED" | "CANCELLED";

export interface AdminPurchaseAccessKey {
  id: string;
  keyPrefix: string;
  validFrom: string;
  validUntil: string;
  activatedAt: string | null;
  lastUsedAt: string | null;
  useCount: number;
  revokedAt: string | null;
  revokedReason: string | null;
}
export interface AdminPurchase {
  id: string;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  source: TicketSource;
  status: TicketStatus;
  externalId: string;
  productCode: string;
  exhibitionSlug: string | null;
  email: string | null;
  purchasedAt: string;
  validFrom: string | null;
  validUntil: string | null;
  /** Date de visite prévue : celle du revendeur, sinon la séance See Tickets. */
  visitAt: string | null;
  showName: string | null;
  createdAt: string;
  /** Code comptoir (unitaire, V1.34) : nature, valeur, état, adresse du QR. Null sinon. */
  counter: AdminPurchaseCounter | null;
  accessKey: AdminPurchaseAccessKey | null;
}
export interface AdminPurchaseCounter {
  kind: CounterKind;
  kindLabel: string;
  value: string | null;
  url: string | null;
  state: CounterCodeState;
  device: string | null;
  reason: string | null;
}

/** Filtres de l'écran Achats et accès (V1.82). */
export interface PurchaseFilters {
  q?: string;
  source?: string;
  state?: string;
  exhibition?: string;
  purchaseFrom?: string;
  purchaseTo?: string;
  visitFrom?: string;
  visitTo?: string;
}
export interface PurchasePage {
  total: number;
  limit: number;
  offset: number;
  purchases: AdminPurchase[];
}
export function listPurchases(filters: PurchaseFilters, limit: number, offset: number) {
  const qs = new URLSearchParams({ paged: "1", limit: String(limit), offset: String(offset) });
  for (const [k, v] of Object.entries(filters)) {
    if (v) qs.set(k, v);
  }
  return request<PurchasePage>(`/admin/sav/purchases?${qs}`);
}

export interface PurchaseFacets {
  sources: Array<{ value: string; count: number }>;
  states: Array<{ value: string; count: number }>;
  exhibitions: Array<{ value: string; label: string; count: number }>;
}
export function getPurchaseFacets() {
  return request<PurchaseFacets>("/admin/sav/purchases/facets");
}

/** Accès manuel = code unitaire (V1.34) : même billet qu'un code comptoir, sans lot. */
export interface CreateManualPurchaseArgs {
  exhibitionId: string;
  kind: CounterKind;
  reason: string;
  email?: string;
}
export interface ManualPurchaseResponse {
  ticketId: string;
  value: string;
  url: string;
  appUrl: string;
  kind: CounterKind;
  kindLabel: string;
  exhibition: { slug: string; title: string };
  establishment: { id: string; slug: string; name: string };
}
export function createManualPurchase(args: CreateManualPurchaseArgs) {
  return request<ManualPurchaseResponse>("/admin/sav/purchases", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export function revokePurchase(ticketId: string, reason?: string) {
  return request<void>(`/admin/sav/purchases/${ticketId}/revoke`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function getPurchaseAccessUrl(ticketId: string) {
  return request<{ accessUrl: string; externalId: string }>(
    `/admin/sav/purchases/${ticketId}/access-url`,
  );
}

// =============================================================================
// Demo Access — allowlist d'emails autorisés sur la PWA visiteur publique
// =============================================================================

export interface AdminDemoAccessEmail {
  id: string;
  email: string;
  notes: string | null;
  active: boolean;
  /** Sprint ED-4 : Client de rattachement (null = global / tous Clients). */
  tenantSlug: string | null;
  tenantName: string | null;
  addedBy: string | null;
  lastUsedAt: string | null;
  useCount: number;
  createdAt: string;
}

export function listDemoAccess() {
  return request<AdminDemoAccessEmail[]>("/admin/demo-access");
}

export function addDemoAccess(args: { email: string; notes?: string; tenantSlug?: string }) {
  return request<{ id: string; email: string; active: boolean; created: boolean }>(
    "/admin/demo-access",
    { method: "POST", body: JSON.stringify(args) },
  );
}

export function updateDemoAccess(
  id: string,
  args: { active?: boolean; notes?: string | null; tenantSlug?: string | null },
) {
  return request<{ id: string; active: boolean }>(`/admin/demo-access/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteDemoAccess(id: string) {
  return request<void>(`/admin/demo-access/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sprint Z-2 — FAQ admin
// =============================================================================

export interface AdminFaq {
  id: string;
  category: string;
  question: string;
  answer: string;
  displayOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export function listFaqs() {
  return request<AdminFaq[]>("/admin/faqs");
}

export interface FaqInput {
  category: string;
  question: string;
  answer: string;
  displayOrder?: number;
  active?: boolean;
}

export function createFaq(args: FaqInput) {
  return request<{ id: string }>("/admin/faqs", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export function updateFaq(id: string, args: Partial<FaqInput>) {
  return request<void>(`/admin/faqs/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteFaq(id: string) {
  return request<void>(`/admin/faqs/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sprint J-1 — Collections CRUD (V2)
// =============================================================================

export interface AdminCollection {
  id: string;
  slug: string;
  isArchive: boolean;
  active: boolean;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  coverMediaId: string | null;
  coverUrl: string | null;
  titleFr: string;
  titleEn: string | null;
  descriptionFr: string | null;
  descriptionEn: string | null;
  poisCount: number;
  parcoursCount: number;
  // Lot 4 : fenêtre de validité d'accès au contenu
  accessWindowEnabled: boolean;
  accessDaysBefore: number | null;
  accessDaysAfter: number | null;
  createdAt: string;
}

export function listCollections() {
  return request<AdminCollection[]>("/admin/collections");
}

export function listCollectionsForTenant(tenantSlug: string) {
  return request<AdminCollection[]>("/admin/collections", {}, { tenantSlug });
}

export interface CreateCollectionArgs {
  tenantSlug: string;
  // Sprint AO-2 : rattachement Etablissement (optionnel)
  establishmentId?: string;
  slug: string;
  titleFr: string;
  titleEn?: string;
  descriptionFr?: string;
  descriptionEn?: string;
  isArchive?: boolean;
  // Lot 4 : fenêtre de validité d'accès au contenu
  accessWindowEnabled?: boolean;
  accessDaysBefore?: number | null;
  accessDaysAfter?: number | null;
}

export function createCollection(args: CreateCollectionArgs) {
  return request<{ id: string; slug: string }>("/admin/collections", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface UpdateCollectionArgs {
  // Sprint AM : permet de corriger Client + Etab si Collection vide
  tenantSlug?: string;
  establishmentId?: string | null;
  titleFr?: string;
  titleEn?: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  active?: boolean;
  coverMediaId?: string | null;
  // Lot 4 : fenêtre de validité d'accès au contenu
  accessWindowEnabled?: boolean;
  accessDaysBefore?: number | null;
  accessDaysAfter?: number | null;
}

export function updateCollection(id: string, args: UpdateCollectionArgs) {
  return request<{ id: string; slug: string }>(`/admin/collections/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteCollection(id: string) {
  return request<void>(`/admin/collections/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sprint DQ — Rooms (Salles) CRUD
// =============================================================================

/** Zone cliquable d'une salle sur le plan, en coordonnées % (0-100). */
export type RoomZone =
  | { kind: "polygon"; points: { x: number; y: number }[] }
  | { kind: "rect"; x: number; y: number; w: number; h: number }
  | null;

export interface AdminTranslationRow {
  id: string;
  field: string;
  localeCode: string;
  value: string;
}

export interface AdminRoom {
  id: string;
  level: string;
  label: string;
  sortOrder: number;
  active: boolean;
  hasZone: boolean;
  tmsZoneIds: string[];
  minArtworks: number | null;
  maxArtworks: number | null;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  coverMediaId: string | null;
  coverUrl: string | null;
  titleFr: string;
  titleEn: string | null;
  poisCount: number;
  createdAt: string;
}

export interface AdminRoomPoi {
  id: string;
  kind: string;
  audioguideCode: string | null;
  museumLevel: string | null;
  museumRoom: string | null;
  titleFr: string | null;
}

export interface AdminRoomDetail {
  id: string;
  level: string;
  label: string;
  sortOrder: number;
  active: boolean;
  zone: RoomZone;
  tmsZoneIds: string[];
  minArtworks: number | null;
  maxArtworks: number | null;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  coverMediaId: string | null;
  coverUrl: string | null;
  translations: AdminTranslationRow[];
  pois: AdminRoomPoi[];
  createdAt: string;
  updatedAt: string;
}

export function listRooms() {
  return request<AdminRoom[]>("/admin/rooms");
}

export function getRoom(id: string) {
  return request<AdminRoomDetail>(`/admin/rooms/${id}`);
}

export interface CreateRoomArgs {
  tenantSlug: string;
  establishmentId: string;
  level: string;
  label: string;
  sortOrder?: number;
  coverMediaId?: string | null;
  titleFr: string;
  titleEn?: string;
  descriptionFr?: string;
  descriptionEn?: string;
}

export function createRoom(args: CreateRoomArgs) {
  return request<{ id: string }>("/admin/rooms", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface UpdateRoomArgs {
  level?: string;
  label?: string;
  sortOrder?: number;
  coverMediaId?: string | null;
  active?: boolean;
  zone?: RoomZone;
  tmsZoneIds?: string[];
  minArtworks?: number | null;
  maxArtworks?: number | null;
  titleFr?: string;
  titleEn?: string;
  descriptionFr?: string;
  descriptionEn?: string;
}

export function updateRoom(id: string, args: UpdateRoomArgs) {
  return request<{ id: string }>(`/admin/rooms/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteRoom(id: string) {
  return request<void>(`/admin/rooms/${id}`, { method: "DELETE" });
}

// =============================================================================
// 28/10/2026 — Salles et TMS : import des plans, synchronisation, couverture
// =============================================================================

export interface PlanImportRoom {
  zoneKey: string;
  tmsZoneIds: string[];
  label: string;
  action: "create" | "update";
  existingId: string | null;
  existingLabel: string | null;
}

export interface PlanImportLevel {
  level: string;
  label: string;
  services: number;
  routes: string[];
  warnings: string[];
  rooms: PlanImportRoom[];
}

export interface PlanImportResult {
  committed: boolean;
  levels: PlanImportLevel[];
  warnings: string[];
  roomsCreated?: number;
  roomsUpdated?: number;
}

export function importPlans(
  establishmentId: string,
  files: Array<{ filename: string; content: string }>,
  commit: boolean,
) {
  return request<PlanImportResult>(`/admin/establishments/${establishmentId}/plans/import`, {
    method: "POST",
    body: JSON.stringify({ files, commit }),
  });
}

export interface TmsRoomRef {
  id: string;
  label: string;
  level: string;
}

export interface TmsChange {
  poiId: string;
  audioguideCode: string | null;
  title: string | null;
  objectIds: string[];
  kind: "arrived" | "moved" | "left" | "unchanged" | "absent";
  fromZoneId: string | null;
  toZoneId: string | null;
  fromRoom: TmsRoomRef | null;
  toRoom: TmsRoomRef | null;
  orphan: boolean;
}

export interface TmsRoomAlert {
  roomId: string;
  label: string;
  level: string;
  count: number;
  minArtworks: number | null;
  maxArtworks: number | null;
  state: "low" | "high";
}

export interface TmsSyncRun {
  id: string;
  state: "DRY_RUN" | "APPLIED" | "FAILED";
  sourceName: string;
  sourceUrl: string | null;
  rowCount: number;
  matchedCount: number;
  arrivedCount: number;
  movedCount: number;
  leftCount: number;
  unchangedCount: number;
  orphanCount: number;
  unmatchedCount: number;
  changeRatio: number;
  message: string | null;
  createdAt: string;
  createdBy: string | null;
  appliedAt: string | null;
  appliedBy: string | null;
}

export interface TmsSyncDiff {
  changes: TmsChange[];
  unmatched: Array<{ poiId: string; audioguideCode: string | null; title: string | null }>;
  orphanZones: Array<{ zoneId: string; label: string | null; level: string | null; pois: number }>;
  alerts: TmsRoomAlert[];
  roomLabels: Array<{ roomId: string; from: string; to: string }>;
  warnings: string[];
  columns: Record<string, string>;
  firstRun: boolean;
  warnRatio: number;
}

export function tmsDryRun(file: File) {
  const form = new FormData();
  form.append("file", file);
  return request<{ run: TmsSyncRun; diff: TmsSyncDiff | null }>("/admin/tms/dry-run", {
    method: "POST",
    body: form,
  });
}

/** Variante : l'adresse du fichier, collée depuis l'email du musée. */
export function tmsDryRunUrl(url: string) {
  return request<{ run: TmsSyncRun; diff: TmsSyncDiff | null }>("/admin/tms/dry-run", {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export function tmsApply(args: { runId: string; poiIds?: string[]; applyRoomLabels?: boolean }) {
  return request<{
    applied: number;
    realigned: number;
    alerts: TmsRoomAlert[];
    alertEmails: number;
  }>("/admin/tms/apply", { method: "POST", body: JSON.stringify(args) });
}

export function listTmsRuns() {
  return request<{ items: TmsSyncRun[] }>("/admin/tms/runs");
}

export interface TmsCoverageRoom {
  id: string;
  label: string;
  level: string;
  tmsZoneIds: string[];
  /** Les seuils PROPRES à la salle. null = elle suit ceux du musée. */
  minArtworks: number | null;
  maxArtworks: number | null;
  /** Ceux qui s'appliquent vraiment, défaut du musée compris. */
  effectiveMin: number | null;
  effectiveMax: number | null;
  count: number;
  state: "ok" | "low" | "high";
  works: Array<{
    id: string;
    audioguideCode: string | null;
    title: string | null;
    active: boolean;
    tmsPinned: boolean;
  }>;
}

export interface TmsCoverage {
  /** Seuils proposés à toutes les salles du musée. */
  defaults: { minArtworks: number | null; maxArtworks: number | null };
  rooms: TmsCoverageRoom[];
  outOfRoom: Array<{ id: string; audioguideCode: string | null; title: string | null; active: boolean }>;
  orphanZones: Array<{ zoneId: string; pois: number }>;
  unmatched: Array<{ id: string; audioguideCode: string | null; title: string | null }>;
}

// =============================================================================
// 28/10/2026 — Diaporamas synchronises : import en masse et vue d'ensemble
// =============================================================================

export interface SlideshowImportRow {
  rowNum: number;
  poi?: string | null;
  imageFile?: string | null;
  order?: number | null;
  cueWord?: string | null;
  credit?: string | null;
  starts?: Record<string, string>;
  ends?: Record<string, string>;
  descriptions?: Record<string, string>;
}

export interface SlideshowPlannedRow {
  rowNum: number;
  poiRef: string | null;
  imageRef: string | null;
  isEnd: boolean;
  poiId: string | null;
  poiTitle: string | null;
  poiMediaId: string | null;
  order: number;
  cueWord: string | null;
  credit: string | null;
  descriptions: Record<string, string>;
  cues: Array<{ locale: string; audioPoiMediaId: string; atMs: number }>;
  warnings: string[];
}

export interface SlideshowPlannedPoi {
  poiId: string;
  title: string | null;
  code: string | null;
  images: string[];
  tracks: Array<{
    audioPoiMediaId: string;
    locale: string | null;
    cues: Array<{ poiMediaId: string; atMs: number; word: string | null }>;
  }>;
  declaredEnds: Array<{ locale: string; atMs: number }>;
  warnings: string[];
}

export interface SlideshowImportResult {
  committed: boolean;
  rows: SlideshowPlannedRow[];
  pois: SlideshowPlannedPoi[];
  unmatched: number;
  locales: string[];
  warnings: string[];
  poisWritten?: number;
  cuesWritten?: number;
  altsWritten?: number;
  creditsWritten?: number;
}

export function importSlideshows(args: {
  rows: SlideshowImportRow[];
  commit?: boolean;
  fps?: number;
  trackRole?: "MAIN_AUDIO" | "KIDS_AUDIO";
  poiIds?: string[];
}) {
  return request<SlideshowImportResult>("/admin/slideshows/import", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface SlideshowOverviewItem {
  id: string;
  audioguideCode: string | null;
  title: string | null;
  establishmentId: string | null;
  establishmentName: string | null;
  enabled: boolean;
  images: number;
  tracks: Array<{ locale: string | null; cues: number; complete: boolean }>;
  collectionIds: string[];
  exhibitionIds: string[];
  parcoursIds: string[];
}

/**
 * À l'échelle du client, comme les Points d'intérêt : l'établissement choisi en
 * haut du back-office n'est qu'un filtre par défaut, pas une condition.
 */
export function listSlideshows() {
  return request<{ items: SlideshowOverviewItem[] }>("/admin/slideshows");
}

export function getTmsCoverage() {
  return request<TmsCoverage>("/admin/tms/coverage");
}

/**
 * Les seuils proposés à toutes les salles. `resetRooms` remet en plus les
 * salles qui ont les leurs sur ceux-ci — geste explicite, jamais un effet de
 * bord de l'enregistrement.
 */
export function setTmsDefaults(args: {
  minArtworks: number | null;
  maxArtworks: number | null;
  resetRooms?: boolean;
}) {
  return request<{ minArtworks: number | null; maxArtworks: number | null; cleared: number }>(
    "/admin/tms/defaults",
    { method: "PUT", body: JSON.stringify(args) },
  );
}

// =============================================================================
// Sprint M — Establishments CRUD
// =============================================================================

// Sprint AP : plan d'un niveau (etage) uploade au niveau Establishment
export interface FloorPlan {
  level: string;        // ex: "0", "RDC", "2", "-1"
  label: string;        // ex: "Rez-de-chaussee", "Niveau median"
  mediaId: string;
  sortOrder: number;
  url: string | null;   // URL publique du plan (resolue cote backend)
}

export interface AdminEstablishment {
  id: string;
  slug: string;
  name: string;
  shortCode: string | null;
  active: boolean;
  // Sprint DK : crédits / mentions (texte libre, affiché onglet Infos PWA).
  credits: string | null;
  tenant: { slug: string; name: string; shortCode: string };
  logoMediaId: string | null;
  logoUrl: string | null;
  // Sprint AP : plans des niveaux pour la PWA visiteur (bouton "Plan du parcours")
  floorPlans: FloorPlan[];
  collectionsCount: number;
  exhibitionsCount: number;
  poisCount: number;
  mediasCount: number;
  ticketsCount: number;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Infos visiteur (admin V1.28) : FAQ visiteur et informations pratiques d'un
// établissement — Contenus › Infos visiteur. Forme identique à celle que lit
// la PWA : [{ title: { <locale>: texte }, body: { <locale>: texte } }].
// ---------------------------------------------------------------------------

export type VisitorInfoKind = "practical-info" | "faq";

export interface VisitorSection {
  title: Record<string, string>;
  body: Record<string, string>;
}

export interface VisitorInfo {
  establishment: { id: string; slug: string; name: string; tenantSlug: string };
  practicalInfo: VisitorSection[];
  faq: VisitorSection[];
  /** FR, EN, puis les langues des applications de l'établissement. */
  languages: string[];
  /** Applications en ligne de l'établissement (version figée à re-publier). */
  liveApps: { id: string; slug: string; displayName: string; publishedAt: string | null }[];
  history: {
    id: string;
    at: string;
    actor: string | null;
    kind: VisitorInfoKind | null;
    event: "saved" | "reverted";
    count: number | null;
    previousCount: number | null;
  }[];
}

export function getVisitorInfo(establishmentId: string) {
  return request<VisitorInfo>(`/admin/establishments/${encodeURIComponent(establishmentId)}/visitor-info`);
}

export function saveVisitorInfo(establishmentId: string, kind: VisitorInfoKind, sections: VisitorSection[]) {
  return request<{ ok: true; unchanged: boolean; count: number }>(
    `/admin/establishments/${encodeURIComponent(establishmentId)}/visitor-info/${kind}`,
    { method: "PUT", body: JSON.stringify({ sections }) },
  );
}

export function revertVisitorInfo(establishmentId: string, kind: VisitorInfoKind) {
  return request<{ ok: true; count: number }>(
    `/admin/establishments/${encodeURIComponent(establishmentId)}/visitor-info/${kind}/revert`,
    { method: "POST" },
  );
}

export function listEstablishments() {
  return request<AdminEstablishment[]>("/admin/establishments");
}

export interface CreateEstablishmentArgs {
  tenantSlug: string;
  slug: string;
  name: string;
  shortCode?: string;
}

export function createEstablishment(args: CreateEstablishmentArgs) {
  return request<{ id: string; slug: string }>("/admin/establishments", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface UpdateEstablishmentArgs {
  name?: string;
  shortCode?: string | null;
  active?: boolean;
  logoMediaId?: string | null;
  // Sprint DK : crédits / mentions.
  credits?: string | null;
  // Sprint AP : plans des niveaux. La cle url N'EST PAS envoyee (le backend
  // la re-resout). Seuls level/label/mediaId/sortOrder sont persistes.
  floorPlans?: Array<{
    level: string;
    label: string;
    mediaId: string;
    sortOrder: number;
  }>;
}

export function updateEstablishment(id: string, args: UpdateEstablishmentArgs) {
  return request<{ id: string; slug: string }>(`/admin/establishments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteEstablishment(id: string) {
  return request<void>(`/admin/establishments/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sprint J-3 — Mini-jeux CRUD (V2)
// =============================================================================

export type MiniGameKind = "QUIZ" | "MOLETTE" | "POINT_CLICK" | "CUSTOM";

export interface AdminMiniGame {
  id: string;
  slug: string;
  kind: MiniGameKind;
  active: boolean;
  tenant: { slug: string; name: string; shortCode: string };
  titleFr: string;
  titleEn: string | null;
  bundleStorageKey: string | null;
  bundleVersion: string | null;
  bundleSize: number | null;
  hasBundle: boolean;
  /** Bundle uploade (CUSTOM) ou contenu rempli (integres) — pret a jouer. */
  configured?: boolean;
  /** Noms des PwaProjects qui incluent ce mini-jeu (vide = inclus nulle part). */
  includedIn?: string[];
  thumbnailMediaId: string | null;
  createdAt: string;
}

export function listMiniGames() {
  return request<AdminMiniGame[]>("/admin/mini-games");
}

export function listMiniGamesForTenant(tenantSlug: string) {
  return request<AdminMiniGame[]>("/admin/mini-games", {}, { tenantSlug });
}

export interface AdminMiniGameDetail {
  id: string;
  slug: string;
  kind: MiniGameKind;
  active: boolean;
  propsSchema: unknown;
  defaultProps: unknown;
}

export function getMiniGame(id: string) {
  return request<AdminMiniGameDetail>(`/admin/mini-games/${id}`);
}

export interface CreateMiniGameArgs {
  tenantSlug: string;
  slug: string;
  kind: MiniGameKind;
  titleFr: string;
  titleEn?: string;
}

export function createMiniGame(args: CreateMiniGameArgs) {
  return request<{ id: string; slug: string }>("/admin/mini-games", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface UpdateMiniGameArgs {
  titleFr?: string;
  titleEn?: string;
  active?: boolean;
  defaultProps?: unknown;
  propsSchema?: unknown;
  thumbnailMediaId?: string | null;
}

export function updateMiniGame(id: string, args: UpdateMiniGameArgs) {
  return request<{ id: string; slug: string }>(`/admin/mini-games/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteMiniGame(id: string) {
  return request<void>(`/admin/mini-games/${id}`, { method: "DELETE" });
}

export interface UploadMiniGameBundleResult {
  id: string;
  slug: string;
  bundleStorageKey: string;
  bundleVersion: string;
  bundleSize: number;
  bundleUrl: string;
}

export async function uploadMiniGameBundle(
  miniGameId: string,
  file: File,
): Promise<UploadMiniGameBundleResult> {
  const body = new FormData();
  body.append("file", file);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/admin/mini-games/${miniGameId}/bundle`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadMiniGameBundleResult);
        } catch {
          reject(new ApiError("Invalid JSON response", xhr.status));
        }
      } else {
        if (xhr.status === 401) { clearToken(); redirectToLogin("expired"); }
        let msg = `${xhr.status} ${xhr.statusText}`;
        try {
          const body = JSON.parse(xhr.responseText) as ApiErrorPayload;
          if (body.message) msg = body.message;
        } catch {
          /* ignore */
        }
        reject(new ApiError(msg, xhr.status));
      }
    });
    xhr.addEventListener("error", () => reject(new ApiError("Network error", 0)));
    xhr.send(body);
  });
}

// =============================================================================
// Sprint Chasse au tresor — TreasureHunt CRUD (parcours enfant generique)
// =============================================================================

/** Map locale -> texte, ex { fr: "...", en: "..." }. */
export type Localized = Record<string, string>;
export type TreasureHuntMechanic = "QUIZ" | "MOLETTE" | "POINTCLICK";

/** Reference image d'un contenu de jeu : media uploade (storageKey) ou URL brute. */
export interface GameImage {
  id?: string;
  storageKey?: string;
  src?: string;
  alt?: Localized;
}
export interface HuntQuizContent {
  type: "quiz";
  question: Localized;
  image?: GameImage;
  answers: { id: string; label: Localized; correct?: boolean; feedback?: Localized }[];
}
export interface HuntMoletteContent {
  type: "molette";
  prompt: Localized;
  image?: GameImage;
  options: { id: string; label: Localized; emoji?: string; correct?: boolean }[];
}
export interface HuntPointClickContent {
  type: "pointclick";
  prompt: Localized;
  image: GameImage;
  /** Rectangle { xPct,yPct,wPct,hPct } (trace) ou cercle legacy { xPct,yPct,radiusPct }. */
  target: { xPct: number; yPct: number; radiusPct?: number; wPct?: number; hPct?: number };
  hint?: Localized;
}
export type HuntStepContent = HuntQuizContent | HuntMoletteContent | HuntPointClickContent;

export interface HuntClue {
  label?: Localized;
  emoji?: string;
}

export interface AdminTreasureHuntListItem {
  id: string;
  slug: string;
  active: boolean;
  tenant: { slug: string; name: string; shortCode: string };
  titleFr: string;
  stepsCount: number;
  createdAt: string;
}

export interface AdminTreasureHuntStep {
  id: string;
  sortOrder: number;
  mechanic: TreasureHuntMechanic;
  poiId: string | null;
  poiTitleFr: string | null;
  poiAudioguideCode: string | null;
  content: HuntStepContent;
  clue: HuntClue;
  clueMediaId: string | null;
}

export interface AdminTreasureHuntDetail {
  id: string;
  slug: string;
  active: boolean;
  tenant: { slug: string; name: string; shortCode: string };
  title: Localized;
  reward: { title?: Localized; text?: Localized; emoji?: string };
  rewardMediaId: string | null;
  mascotMediaId: string | null;
  mascotUrl: string | null;
  themeColor: string | null;
  steps: AdminTreasureHuntStep[];
  createdAt: string;
  updatedAt: string;
}

export function listTreasureHunts() {
  return request<AdminTreasureHuntListItem[]>("/admin/treasure-hunts");
}

export function listTreasureHuntsForTenant(tenantSlug: string) {
  return request<AdminTreasureHuntListItem[]>("/admin/treasure-hunts", {}, { tenantSlug });
}

export function getTreasureHunt(id: string) {
  return request<AdminTreasureHuntDetail>(`/admin/treasure-hunts/${id}`);
}

export interface CreateTreasureHuntArgs {
  tenantSlug: string;
  slug: string;
  titleFr: string;
  titleEn?: string;
}

export function createTreasureHunt(args: CreateTreasureHuntArgs) {
  return request<{ id: string }>("/admin/treasure-hunts", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface UpdateTreasureHuntArgs {
  title?: Localized;
  reward?: { title?: Localized; text?: Localized; emoji?: string };
  rewardMediaId?: string | null;
  mascotMediaId?: string | null;
  themeColor?: string | null;
  active?: boolean;
}

export function updateTreasureHunt(id: string, args: UpdateTreasureHuntArgs) {
  return request<{ id: string }>(`/admin/treasure-hunts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteTreasureHunt(id: string) {
  return request<void>(`/admin/treasure-hunts/${id}`, { method: "DELETE" });
}

export interface UpsertHuntStepArgs {
  content: HuntStepContent;
  clue: HuntClue;
  poiId?: string | null;
  clueMediaId?: string | null;
  sortOrder?: number;
}

export function createTreasureHuntStep(huntId: string, args: UpsertHuntStepArgs) {
  return request<{ id: string }>(`/admin/treasure-hunts/${huntId}/steps`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export function updateTreasureHuntStep(
  huntId: string,
  stepId: string,
  args: Partial<UpsertHuntStepArgs>,
) {
  return request<{ id: string }>(`/admin/treasure-hunts/${huntId}/steps/${stepId}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

export function deleteTreasureHuntStep(huntId: string, stepId: string) {
  return request<void>(`/admin/treasure-hunts/${huntId}/steps/${stepId}`, { method: "DELETE" });
}

export function reorderTreasureHuntSteps(huntId: string, order: string[]) {
  return request<{ id: string }>(`/admin/treasure-hunts/${huntId}/steps-order`, {
    method: "PATCH",
    body: JSON.stringify({ order }),
  });
}

// =============================================================================
// Sprint K — PwaProjects CRUD + publish lifecycle
// =============================================================================

export type PwaProjectState = "DRAFT" | "LIVE";
// Point 4 : type de PWA (STANDARD complet vs MONO mono-parcours).
export type PwaProjectKind = "STANDARD" | "MONO";
export type PwaProjectIncludeKind =
  | "COLLECTION"
  | "EXHIBITION"
  | "MINI_GAME"
  | "PARCOURS"
  | "TREASURE_HUNT";

// Sprint AN-3 : statut du provisioning Cloudflare (Pages domains + DNS)
export type HostingStatus = "PENDING" | "PROVISIONING" | "READY" | "ERROR";

export interface AdminPwaProjectListItem {
  id: string;
  slug: string;
  displayName: string;
  state: PwaProjectState;
  kind: PwaProjectKind;
  tenant: { slug: string; name: string; shortCode: string };
  establishment: { id: string; slug: string; name: string } | null;
  includesCount: number;
  publishedAt: string | null;
  publishedBy: string | null;
  previewUrl: string;
  productionUrl: string | null;
  // Sprint AN-3 : hebergement Cloudflare
  hostingStatus: HostingStatus;
  hostingError: string | null;
  hostingProvisionedAt: string | null;
  hostingProvisionedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function listPwaProjects() {
  return request<AdminPwaProjectListItem[]>("/admin/pwa-projects");
}

export interface AdminPwaProjectInclude {
  id: string;
  kind: PwaProjectIncludeKind;
  targetId: string;
  targetSlug: string | null;
  label: string | null;
  sortOrder: number;
  visible: boolean;
  isArchive?: boolean;
  type?: "PERMANENT" | "TEMPORARY" | null;
  hasBundle?: boolean;
  // Mecanique du mini-jeu (MINI_GAME) : QUIZ/MOLETTE/POINT_CLICK/CUSTOM.
  // (auparavant expose par erreur sous `kind`, ce qui ecrasait le kind de
  // l'inclusion et faisait disparaitre les mini-jeux du regroupement.)
  gameKind?: string | null;
  // Sprint chasse au tresor : nombre d'etapes (include kind TREASURE_HUNT)
  stepsCount?: number;
  // Sprint CJ-2 / CM-2 / CY-3 : metadata des includes PARCOURS
  scope?: "PERMANENT" | "TEMPORARY" | null;
  parentKind?: "COLLECTION" | "EXHIBITION" | null;
  parentLabel?: string | null;
  // Sprint CY-3 : ajout "CUSTOM" + customProfileName pour le display
  profile?: "CLASSIC" | "KIDS" | "LSF" | "ACCESSIBILITY" | "CUSTOM" | null;
  customProfileName?: string | null;
}

export interface AdminPwaProjectDetail {
  id: string;
  slug: string;
  displayName: string;
  state: PwaProjectState;
  kind: PwaProjectKind;
  tenant: {
    id: string;
    slug: string;
    name: string;
    shortCode: string;
    logoMediaId: string | null;
    logoUrl: string | null;
  };
  establishment: {
    id: string;
    slug: string;
    name: string;
    shortCode: string | null;
    logoMediaId: string | null;
    logoUrl: string | null;
  } | null;
  customization: Record<string, unknown> | null;
  logoMediaId: string | null;
  previewUrl: string;
  productionUrl: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  // Sprint AN-3 : hebergement Cloudflare
  hostingStatus: HostingStatus;
  hostingError: string | null;
  hostingProvisionedAt: string | null;
  hostingProvisionedBy: string | null;
  includes: AdminPwaProjectInclude[];
  createdAt: string;
  updatedAt: string;
}

export function getPwaProject(id: string) {
  return request<AdminPwaProjectDetail>(`/admin/pwa-projects/${id}`);
}

// Sprint AN-3 : re-trigger manuel du provisioning Cloudflare
export function provisionPwaHosting(projectId: string) {
  return request<{ ok: boolean; status: HostingStatus }>(
    `/admin/pwa-projects/${projectId}/provision-hosting`,
    { method: "POST" },
  );
}

export interface CreatePwaProjectArgs {
  tenantSlug: string;
  establishmentId?: string;
  slug: string;
  displayName: string;
  kind?: PwaProjectKind;
  customization?: Record<string, unknown>;
}

export function createPwaProject(args: CreatePwaProjectArgs) {
  return request<{ id: string; slug: string }>("/admin/pwa-projects", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export interface UpdatePwaProjectArgs {
  establishmentId?: string | null;
  displayName?: string;
  customization?: Record<string, unknown>;
  logoMediaId?: string | null;
}

export function updatePwaProject(id: string, args: UpdatePwaProjectArgs) {
  return request<{ id: string; slug: string }>(`/admin/pwa-projects/${id}`, {
    method: "PATCH",
    body: JSON.stringify(args),
  });
}

// Point 4/5 : duplication d'un PwaProject (customization + inclusions clonees).
export interface DuplicatePwaProjectArgs {
  slug: string;
  displayName: string;
  kind?: PwaProjectKind;
}
export function duplicatePwaProject(id: string, args: DuplicatePwaProjectArgs) {
  return request<{ id: string; slug: string }>(`/admin/pwa-projects/${id}/duplicate`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export function deletePwaProject(id: string) {
  return request<void>(`/admin/pwa-projects/${id}`, { method: "DELETE" });
}

export function addPwaProjectInclude(
  projectId: string,
  args: { kind: PwaProjectIncludeKind; targetId: string; sortOrder?: number },
) {
  return request<{ id: string }>(`/admin/pwa-projects/${projectId}/includes`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export function removePwaProjectInclude(projectId: string, includeId: string) {
  return request<void>(`/admin/pwa-projects/${projectId}/includes/${includeId}`, {
    method: "DELETE",
  });
}

// Sprint Y-3 : reorder DnD
export function reorderPwaProjectIncludes(projectId: string, orderedIncludeIds: string[]) {
  return request<void>(`/admin/pwa-projects/${projectId}/includes/reorder`, {
    method: "POST",
    body: JSON.stringify({ orderedIncludeIds }),
  });
}

export interface PublishPwaProjectResult {
  id: string;
  slug: string;
  state: "LIVE";
  publishedAt: string;
  snapshotSize: number;
}

// ---------------------------------------------------------------------------
// Fiche application › Billetterie (admin V1.31) : canaux de billets acceptés
// et tarifs See Tickets reçus (30 jours) avec l'exposition qu'ils ouvrent.
// ---------------------------------------------------------------------------

export type TicketingSource = "SEETICKETS" | "OCTO" | "MANUAL";

export interface PwaTicketing {
  /** Vide = tous les canaux. */
  acceptedTicketSources: TicketingSource[];
  sources: TicketingSource[];
  establishment: { id: string; name: string } | null;
  days: number;
  received: Record<TicketingSource, number>;
  tariffs: {
    tariff: string;
    exhibitionSlug: string | null;
    exhibitionTitle: string | null;
    physical: boolean;
    count: number;
    lastAt: string;
  }[];
}

export function getPwaTicketing(id: string) {
  return request<PwaTicketing>(`/admin/pwa-projects/${encodeURIComponent(id)}/ticketing`);
}

export function savePwaTicketing(id: string, acceptedTicketSources: TicketingSource[]) {
  return request<{ ok: true; unchanged: boolean; acceptedTicketSources: TicketingSource[] }>(
    `/admin/pwa-projects/${encodeURIComponent(id)}/ticketing`,
    { method: "PUT", body: JSON.stringify({ acceptedTicketSources }) },
  );
}

export function publishPwaProject(id: string) {
  return request<PublishPwaProjectResult>(`/admin/pwa-projects/${id}/publish`, {
    method: "POST",
  });
}

export function unpublishPwaProject(id: string) {
  return request<void>(`/admin/pwa-projects/${id}/unpublish`, { method: "POST" });
}

// =============================================================================
// Partenaires (canaux de vente) — ecran unique Billetterie > Partenaires.
// Deux natures : INGESTION (See Tickets pousse ses billets) et RESELLER
// (Headout, Tiqets, GetYourGuide appellent notre API OCTO, ALTO emet le code).
// =============================================================================

export interface PartnerKey {
  id: string;
  keyPrefix: string;
  testMode: boolean;
  label: string | null;
  revoked: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface PartnerSummary {
  kind: "INGESTION" | "RESELLER";
  slug: string;
  name: string;
  codePrefix: string;
  active: boolean;
  products: string[];
  keys: PartnerKey[];
  ticketsTotal: number | null;
  sales: { confirmed: number; cancelled: number } | null;
}

export interface PartnerProduct {
  id: string;
  name: Record<string, string>;
  reference: string;
  pwaProjectSlug?: string;
  parcoursSlugs?: string[];
  exhibitionSlugs?: string[];
  active?: boolean;
  /** Jours reglés sur le PRODUIT (repère). null = réglage du musée. */
  productDaysBefore?: number | null;
  productDaysAfter?: number | null;
  /** Jours NÉGOCIÉS avec ce revendeur. null = on hérite du produit. */
  accessDaysBefore?: number | null;
  accessDaysAfter?: number | null;
  /** Reporting lot 2 : prix TTC du produit (repère), prix propre à CE
   *  revendeur (null = celui du produit), ouverture de ses ventes. */
  productUnitPriceCents?: number | null;
  unitPriceCents?: number | null;
  salesOpenAt?: string | null;
}

export interface PartnerBooking {
  uuid: string;
  supplierReference: string;
  resellerReference: string | null;
  status: string;
  testMode: boolean;
  productId: string;
  visitDate: string;
  createdAt: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** Au moins un code déjà ouvert sur un téléphone. */
  opened: boolean;
  codes: { value: string; active: boolean }[];
}

export interface PartnerDetail {
  slug: string;
  name: string;
  codePrefix: string;
  active: boolean;
  keys: PartnerKey[];
  products: PartnerProduct[];
  availableProducts: { id: string; name: Record<string, string>; reference: string }[];
  bookings: PartnerBooking[];
}

export function listPartners() {
  return request<{ octoEnabled: boolean; partners: PartnerSummary[] }>("/admin/partners");
}

export function getPartner(slug: string) {
  return request<PartnerDetail>(`/admin/partners/${encodeURIComponent(slug)}`);
}

export function createPartner(body: { slug: string; name: string; codePrefix: string }) {
  return request<{ slug: string }>("/admin/partners", { method: "POST", body: JSON.stringify(body) });
}

export function updatePartner(slug: string, body: { name?: string; active?: boolean }) {
  return request<{ slug: string }>(`/admin/partners/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** La cle n'est renvoyee QU'ICI : seule son empreinte est conservee. */
export function createPartnerKey(slug: string, body: { testMode: boolean; label?: string }) {
  return request<{ key: string; testMode: boolean }>(`/admin/partners/${encodeURIComponent(slug)}/keys`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function revokePartnerKey(slug: string, id: string) {
  return request<{ ok: boolean }>(
    `/admin/partners/${encodeURIComponent(slug)}/keys/${encodeURIComponent(id)}/revoke`,
    { method: "POST" },
  );
}

/** Révoque une réservation revendeur : la réservation passe CANCELLED, ses codes sont coupés. */
export function revokePartnerBooking(slug: string, uuid: string, reason: string) {
  return request<{ ok: boolean; alreadyCancelled: boolean }>(
    `/admin/partners/${encodeURIComponent(slug)}/bookings/${encodeURIComponent(uuid)}/revoke`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

/** Autorise un produit — et pose/renégocie les jours d'accès de ce revendeur. */
export function grantPartnerProduct(
  slug: string,
  productId: string,
  jours?: {
    accessDaysBefore?: number | null;
    accessDaysAfter?: number | null;
    unitPriceCents?: number | null;
    salesOpenAt?: string | null;
  },
) {
  return request<{ ok: boolean }>(`/admin/partners/${encodeURIComponent(slug)}/grants`, {
    method: "POST",
    body: JSON.stringify({ productId, ...(jours ?? {}) }),
  });
}

export function revokePartnerProduct(slug: string, productId: string) {
  return request<{ ok: boolean }>(
    `/admin/partners/${encodeURIComponent(slug)}/grants/${encodeURIComponent(productId)}`,
    { method: "DELETE" },
  );
}

export function createPartnerTestCode(
  slug: string,
  body: { productId: string; visitDate?: string; value?: string },
) {
  return request<{
    value: string;
    productId: string;
    validFrom: string;
    validUntil: string;
    urls: { recette: string; public: string };
  }>(
    `/admin/partners/${encodeURIComponent(slug)}/test-codes`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export interface PartnerTestCode {
  keyId: string | null;
  value: string | null;
  productId: string | null;
  visitDate: string | null;
  validFrom: string | null;
  validUntil: string | null;
  createdAt: string;
  uses: number;
  active: boolean;
  urls: { recette: string; public: string } | null;
}

/** Codes de test déjà émis pour ce revendeur (les plus récents d'abord). */
export function listPartnerTestCodes(slug: string) {
  return request<{ codes: PartnerTestCode[] }>(
    `/admin/partners/${encodeURIComponent(slug)}/test-codes`,
  );
}

export function deletePartnerTestCode(slug: string, keyId: string) {
  return request<{ ok: boolean }>(
    `/admin/partners/${encodeURIComponent(slug)}/test-codes/${encodeURIComponent(keyId)}`,
    { method: "DELETE" },
  );
}

export interface OctoProductRow {
  id: string;
  name: Record<string, string>;
  reference: string;
  tenantSlug: string;
  establishment: { slug: string; name: string } | null;
  pwaProjectSlug: string;
  parcoursSlugs: string[];
  exhibitionSlugs: string[];
  active: boolean;
  cancellableAfterRedemption: boolean;
  accessDaysBefore: number | null;
  accessDaysAfter: number | null;
  /** Prix public TTC (centimes), null = inconnu. */
  unitPriceCents: number | null;
  currency: string;
  resellers: { slug: string; name: string }[];
}

export function listOctoProducts() {
  return request<{ products: OctoProductRow[] }>("/admin/octo-products");
}

export function createOctoProduct(body: {
  id: string;
  tenantSlug: string;
  establishmentSlug: string;
  pwaProjectSlug: string;
  reference: string;
  nameFr: string;
  nameEn: string;
  parcoursSlugs: string[];
  exhibitionSlugs: string[];
  accessDaysBefore?: number | null;
  accessDaysAfter?: number | null;
  unitPriceCents?: number | null;
}) {
  return request<{ id: string }>("/admin/octo-products", { method: "POST", body: JSON.stringify(body) });
}

export function updateOctoProduct(
  id: string,
  body: {
    active?: boolean;
    parcoursSlugs?: string[];
    accessDaysBefore?: number | null;
    accessDaysAfter?: number | null;
    unitPriceCents?: number | null;
  },
) {
  return request<{ id: string }>(`/admin/octo-products/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// =============================================================================
// Reporting billetterie (lot 1, 24/09/2026) — cf. apps/api/src/routes/adminReporting.ts
// =============================================================================

export type ReportStep = "day" | "week" | "month" | "quarter" | "year";

/**
 * Ce que la periode compte (V1.77) : la date de VENTE (defaut, base
 * contractuelle des ventes, du CA et de la redevance) ou la date de VISITE
 * prevue. Un mois de visite ne se lit pas autrement : un billet visite en
 * octobre a pu etre achete en aout.
 */
export type ReportBasis = "sale" | "visit";

export interface TicketReportParams {
  /** Dates LOCALES (Paris) incluses, `YYYY-MM-DD`. */
  from: string;
  to: string;
  step: ReportStep;
  partners: string[];
  establishments: string[];
  pwas: string[];
  parcours: string[];
  excludeTests: boolean;
  /** Borner à l'ouverture des ventes de chaque PWA / produit revendeur. */
  cap: boolean;
  /** Défaut `sale` (omis dans la requête) : l'API garde alors son calcul. */
  basis?: ReportBasis;
}

export interface TicketReportGroup {
  period: string;
  partner: string;
  establishmentId: string | null;
  pwaKey: string;
  contentKey: string;
  sold: number;
  cancelled: number;
  net: number;
  grossCents: number;
  netCents: number;
  priced: number;
  activated: number;
  royaltyCents: number;
  ruled: number;
  /** No show (V1.77) : billets nets dont la visite prévue est PASSÉE. */
  visitPast?: number;
  /** Dont jamais activés. Le taux est `noShow / visitPast`. */
  noShow?: number;
}

export interface TicketReportDenialGroup {
  period: string;
  partner: string;
  establishmentId: string | null;
  pwaKey: string;
  reason: string;
  attempts: number;
  codes: number;
  /** Identifiants (propres à la réponse) des codes refusés : leur union donne
   *  des codes distincts exacts. Absent d'une API antérieure au 24/09. */
  codeIds?: number[];
}

export interface TicketReport {
  step: ReportStep;
  from: string;
  to: string;
  /** Base effectivement appliquée. Absente d'une API antérieure au 03/11. */
  basis?: ReportBasis;
  /** Où est passé le temps, côté serveur (03/11) : attente dans la file,
   *  lecture en base, agrégation. Pour diagnostiquer un écran lent sans
   *  accès aux journaux du conteneur. */
  timings?: { queueMs: number; loadMs: number; buildMs: number; totalMs: number; facts: number };
  generatedAt: string;
  periods: string[];
  groups: TicketReportGroup[];
  denialGroups: TicketReportDenialGroup[];
  /** Billets d'audioguide PHYSIQUE transmis par See Tickets sur la période :
   *  EXCLUS des chiffres, affichés en avertissement (24/09/2026). `null` en
   *  base « date de visite » : ils se comptent à la date d'achat. */
  physicalTickets?: number | null;
  /** Codes comptoir (admin V1.33) : stock imprimé et activations, par nature. */
  counterCodes?: CounterSummaryRow[];
  /** Prix Carte Blanche par exposition (V1.35) : tarif See Tickets − 15 %. */
  counterPrices?: { exhibitionSlug: string; baseCents: number; cents: number; discount: number }[];
  totals: {
    sold: number;
    cancelled: number;
    net: number;
    grossCents: number;
    netCents: number;
    priced: number;
    activated: number;
    royaltyCents: number;
    ruled: number;
    visitPast?: number;
    noShow?: number;
    /** Base « visite » : billets nets SANS date de visite, hors de la lecture. */
    undated?: number;
    deniedAttempts: number;
    deniedCodes: number;
  };
  context: {
    pwas: { slug: string; state: PwaProjectState; salesStartAt: string | null }[];
    products: { id: string; reference: string; pwaProjectSlug: string; saleStart: string | null; saleEnd: string | null }[];
  };
}

export interface TicketReportDetail {
  tickets: {
    id: string;
    soldAt: string;
    soldDay: string;
    partner: string;
    establishmentId: string | null;
    pwaSlugs: string[];
    contentKey: string;
    tariff: string | null;
    amountCents: number | null;
    currency: string | null;
    priceSource: "api" | "parametre" | "remise" | null;
    royaltyCents: number | null;
    cancelled: boolean;
    cancelledAt: string | null;
    activatedAt: string | null;
    /** Jour de visite prévu, heure de Paris (V1.40) ; null si inconnu. */
    visitDay?: string | null;
    isTest: boolean;
    code: string | null;
  }[];
  denials: {
    at: string;
    partner: string;
    pwaSlug: string | null;
    establishmentId: string | null;
    reason: string;
    code: string | null;
  }[];
}

/**
 * Périmètre d'un canal de vente (corrections du 24/09/2026) : partenaire × PWA
 * d'un établissement, parcours ouverts, ouverture des ventes. Sert aux filtres
 * en cascade, au rappel des dates et à « Depuis le début ».
 */
export interface ReportingScopeChannel {
  partner: string;
  establishmentId: string | null;
  /** Null : billets reçus pour un musée sans PWA en ligne qui les accepte. */
  pwaSlug: string | null;
  productId: string | null;
  productReference: string | null;
  parcours: string[];
  exhibitions: string[];
  salesStartAt: string | null;
  salesStartSource: "pwa" | "revendeur" | "produit" | null;
  salesEndAt: string | null;
  /** Première vente réelle (tests exclus). */
  firstSaleAt: string | null;
}

/** Périmètre du client (et de l'établissement) de la barre latérale. */
/** Noms des partenaires pour le Reporting (analytics.read, tous rôles). */
export interface ReportingPartner {
  kind: "INGESTION" | "RESELLER";
  slug: string;
  name: string;
  codePrefix: string;
}
export function listReportingPartners() {
  return request<{ partners: ReportingPartner[] }>("/admin/reporting/partners");
}

export function getReportingScope() {
  return request<{ channels: ReportingScopeChannel[] }>("/admin/reporting/scope");
}

function reportQuery(p: TicketReportParams): string {
  const qs = new URLSearchParams({
    from: p.from,
    to: p.to,
    step: p.step,
    excludeTests: p.excludeTests ? "1" : "0",
    cap: p.cap ? "1" : "0",
  });
  // Omis en base « vente » : une API qui ignore le parametre reste correcte.
  if (p.basis === "visit") qs.set("basis", "visit");
  if (p.partners.length) qs.set("partners", p.partners.join(","));
  if (p.establishments.length) qs.set("establishments", p.establishments.join(","));
  if (p.pwas.length) qs.set("pwas", p.pwas.join(","));
  if (p.parcours.length) qs.set("parcours", p.parcours.join(","));
  return qs.toString();
}

/** `signal` : la demande est abandonnée si les filtres changent entre-temps (V1.53). */
export function getTicketReport(p: TicketReportParams, signal?: AbortSignal) {
  return request<TicketReport>(`/admin/reporting/tickets?${reportQuery(p)}`, { signal });
}

export function getTicketReportDetail(p: TicketReportParams) {
  return request<TicketReportDetail>(`/admin/reporting/tickets/detail?${reportQuery(p)}`);
}

// Visites prévues (admin V1.39) — cf. apps/api/src/lib/visitForecast.ts
export interface VisitDay {
  /** Jour de visite prévu, heure de Paris (`YYYY-MM-DD`). */
  day: string;
  partner: string;
  tickets: number;
  /** Dont billets déjà activés. */
  activated: number;
}

export interface VisitForecastGroup {
  tenantId: string;
  /** null = billets « tout le musée » de l'établissement. */
  exhibitionSlug: string | null;
  establishmentId: string | null;
  /** Dates LOCALES de l'exposition (axe du graphique). */
  startDate: string | null;
  endDate: string | null;
  days: VisitDay[];
  total: number;
  upcoming: number;
  /** Billets nets sans date de visite, hors graphique. */
  undated: number;
}

export interface VisitForecast {
  generatedAt: string;
  today: string;
  groups: VisitForecastGroup[];
}

/**
 * Mêmes filtres que la billetterie, sans la période choisie : billets vendus
 * depuis `since` (V1.42 : le début de « Depuis le début », l'ouverture des
 * ventes) jusqu'à aujourd'hui.
 */
export type VisitForecastParams = Omit<TicketReportParams, "from" | "to" | "step"> & { since?: string };

export function getVisitForecast({ since, ...p }: VisitForecastParams, signal?: AbortSignal) {
  const qs = new URLSearchParams(reportQuery({ ...p, from: "", to: "", step: "day" }));
  qs.delete("from");
  qs.delete("to");
  qs.delete("step");
  if (since) qs.set("from", since);
  return request<VisitForecast>(`/admin/reporting/visits?${qs.toString()}`, { signal });
}

// =============================================================================
// Royautés (reporting lot 2, 24/09/2026) — cf. apps/api/src/routes/adminRoyalties.ts
// =============================================================================

export type RoyaltyBase = "TTC" | "HT" | "NET";

export interface RoyaltyRuleView {
  id: string;
  partnerKey: string;
  productId: string | null;
  exhibitionSlug: string | null;
  /** Dates LOCALES incluses. */
  validFrom: string;
  validTo: string | null;
  base: RoyaltyBase;
  vatRatePct: number;
  commissionPct: number;
  ratePct: number;
  notes: string | null;
}

export interface PartnerSettlementView {
  payer: string | null;
  payee: string | null;
  closingEnabled: boolean;
  notes: string | null;
}

export interface RoyaltySettings {
  partnerKey: string;
  tenants: { slug: string; name: string; settlement: PartnerSettlementView | null; rules: RoyaltyRuleView[] }[];
}

export function getRoyaltySettings(partnerKey: string) {
  return request<RoyaltySettings>(`/admin/royalties/settings?partnerKey=${encodeURIComponent(partnerKey)}`);
}

export function saveSettlement(body: {
  tenantSlug: string;
  partnerKey: string;
  payer?: string | null;
  payee?: string | null;
  closingEnabled?: boolean;
  notes?: string | null;
}) {
  return request<{ ok: true; closingEnabled: boolean }>("/admin/royalties/settlement", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function createRoyaltyRule(body: {
  tenantSlug: string;
  partnerKey: string;
  productId?: string | null;
  exhibitionSlug?: string | null;
  validFrom: string;
  validTo?: string | null;
  base: RoyaltyBase;
  vatRatePct: number;
  commissionPct: number;
  ratePct: number;
  notes?: string | null;
}) {
  return request<RoyaltyRuleView>("/admin/royalties/rules", { method: "POST", body: JSON.stringify(body) });
}

export function deleteRoyaltyRule(id: string) {
  return request<{ ok: true }>(`/admin/royalties/rules/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface RoyaltyStatementLine {
  contentKey: string;
  /** Une ligne = un établissement × un contenu (null : relevés d'avant le 24/09/2026). */
  establishmentId?: string | null;
  sold: number;
  cancelled: number;
  net: number;
  unpriced: number;
  unruled: number;
  ttcCents: number;
  htCents: number;
  commissionCents: number;
  baseCents: number;
  royaltyCents: number;
  ruleIds: string[];
}

export interface RoyaltyStatement {
  partnerKey: string;
  status: "closed" | "provisional";
  closable: boolean;
  closing: { id: string; closedAt: string; closedBy: string | null } | null;
  settlement: PartnerSettlementView | null;
  rules: RoyaltyRuleView[];
  statement: {
    lines: RoyaltyStatementLine[];
    regularizations: {
      ticketId: string;
      contentKey: string;
      establishmentId?: string | null;
      fromPeriod: string;
      cancelledAt: string;
      ttcCents: number;
      royaltyCents: number;
    }[];
    totals: {
      sold: number;
      cancelled: number;
      net: number;
      unpriced: number;
      unruled: number;
      ttcCents: number;
      htCents: number;
      commissionCents: number;
      baseCents: number;
      royaltyCents: number;
      regularizationTtcCents: number;
      regularizationRoyaltyCents: number;
      dueCents: number;
    };
  };
}

export function getRoyaltyStatements(
  tenantSlug: string,
  period: string,
  partners: string[],
  establishments: string[] = [],
  signal?: AbortSignal,
) {
  const qs = new URLSearchParams({ tenantSlug, period, partners: partners.join(",") });
  if (establishments.length) qs.set("establishments", establishments.join(","));
  return request<{ tenant: { slug: string; name: string }; period: string; statements: RoyaltyStatement[] }>(
    `/admin/royalties/statements?${qs.toString()}`,
    { signal },
  );
}

export function closeRoyaltyMonth(body: { tenantSlug: string; partnerKey: string; period: string }) {
  return request<{ id: string; closedAt: string }>("/admin/royalties/closings", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function reopenRoyaltyMonth(id: string) {
  return request<{ ok: true }>(`/admin/royalties/closings/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// =============================================================================
// Reporting visiteurs (lot 3, 24/09/2026) — cf. apps/api/src/routes/adminReportingVisitors.ts
// =============================================================================

export interface VisitorReportParams {
  from: string;
  to: string;
  step: ReportStep;
  /** Partenaires, dont `sans-billet` (usage sans billet ouvert sur l'appareil). */
  partners: string[];
  establishments: string[];
  pwas: string[];
  parcours: string[];
  excludeTests: boolean;
  /** Borner chaque évènement aux dates de son exposition (± fenêtre d'accès). */
  capExhibitions: boolean;
}

export interface UsageCounts {
  devices: number;
  sessions: number;
  tickets: number;
  pageViews: number;
  /** Appareils arrêtés sur « Ouvrez ce lien dans Safari » (PWA V0.225 ; absent d'une API plus ancienne). */
  safariGate?: number;
  /** Appareils arrêtés sur « Ouvrez ce lien sur mobile » (PWA V0.225). */
  desktopGate?: number;
  plays: number;
  completes: number;
  parcoursStarts: number;
  parcoursDone: number;
  listenMs: number;
}

export interface VisitorReport {
  from: string;
  to: string;
  step: ReportStep;
  generatedAt: string;
  /** Date depuis laquelle l'usage est rattaché aux billets (lot 0). */
  attributionSince: string;
  periods: string[];
  totals: UsageCounts;
  series: (UsageCounts & { period: string; partner: string })[];
  byPwa: (UsageCounts & { pwaSlug: string })[];
  byPartner: (UsageCounts & { partner: string })[];
  pois: { poiId: string; plays: number; completes: number; listenMs: number; title: string | null; code: string | null; parcours: string[] }[];
  parcours: { slug: string; title: string; starts: number; devices: number; done: number; pois: number }[];
  heatmap: number[][];
  coverage: { events: number; withTicket: number; tests: number };
  funnel: { sold: number; activated: number; used: number; listened: number; done: number } | null;
  languages: { locale: string; devices: number }[];
  exhibitions: { slug: string; startDate: string; endDate: string; windowFrom: string; windowTo: string }[];
}

export function getVisitorReport(p: VisitorReportParams, signal?: AbortSignal) {
  const qs = new URLSearchParams({
    from: p.from,
    to: p.to,
    step: p.step,
    excludeTests: p.excludeTests ? "1" : "0",
    capExhibitions: p.capExhibitions ? "1" : "0",
  });
  if (p.partners.length) qs.set("partners", p.partners.join(","));
  if (p.establishments.length) qs.set("establishments", p.establishments.join(","));
  if (p.pwas.length) qs.set("pwas", p.pwas.join(","));
  if (p.parcours.length) qs.set("parcours", p.parcours.join(","));
  return request<VisitorReport>(`/admin/reporting/visitors?${qs.toString()}`, { signal });
}

// =============================================================================
// Reporting › Qualité (24/09/2026) — incidents de la PWA visiteur.
// Cf. apps/api/src/routes/issues.ts et lib/qualityReport.ts.
// =============================================================================

export interface QualityReportParams {
  from: string;
  to: string;
  step: ReportStep;
  pwas: string[];
  kinds: string[];
  versions: string[];
  platforms: string[];
  excludeTests: boolean;
}

export interface QualityRow {
  key: string;
  count: number;
  devices: number;
  lastAt: string | null;
  /** Déblocage refusé : répartition par motif (V1.43). */
  reasons?: { key: string; count: number }[];
}

export interface QualityIssue {
  id: string;
  at: string;
  kind: string;
  message: string | null;
  context: Record<string, unknown>;
  pwaSlug: string;
  appVersion: string;
  platform: string;
  osVersion: string | null;
  online: boolean;
  standalone: boolean;
  isTest: boolean;
}

export interface QualityReport {
  from: string;
  to: string;
  step: ReportStep;
  generatedAt: string;
  periods: string[];
  totals: {
    issues: number;
    devices: number;
    sessions: number;
    per100Sessions: number | null;
    offline: number;
    standalone: number;
    userReports: number;
  };
  byKind: QualityRow[];
  byVersion: QualityRow[];
  byPlatform: QualityRow[];
  byPwa: (QualityRow & { sessions: number; per100Sessions: number | null })[];
  series: { period: string; kind: string; count: number }[];
  versions: string[];
  latest: QualityIssue[];
  alerts: { id: string; at: string; kind: string; appVersion: string | null; count: number; baseline: number; emailed: boolean }[];
}

export function getQualityReport(p: QualityReportParams, signal?: AbortSignal) {
  const qs = new URLSearchParams({ from: p.from, to: p.to, step: p.step, excludeTests: p.excludeTests ? "1" : "0" });
  if (p.pwas.length) qs.set("pwas", p.pwas.join(","));
  if (p.kinds.length) qs.set("kinds", p.kinds.join(","));
  if (p.versions.length) qs.set("versions", p.versions.join(","));
  if (p.platforms.length) qs.set("platforms", p.platforms.join(","));
  return request<QualityReport>(`/admin/reporting/quality?${qs.toString()}`, { signal });
}

// =============================================================================
// Billetterie et ventes › Codes comptoir (admin V1.33, 25/09/2026)
// =============================================================================

/** « AUTRE » : code unitaire seulement, motif obligatoire (V1.36). */
export type CounterKind = "CARTE_BLANCHE" | "COMPENSATION" | "AUTRE";
export type CounterCodeState = "libre" | "active" | "desactive";
export interface CounterCounts {
  libre: number;
  active: number;
  desactive: number;
}
export interface CounterExhibitionOption {
  id: string;
  slug: string;
  /** Une exposition est toujours temporaire, une collection toujours permanente. */
  kind: "EXHIBITION" | "COLLECTION";
  title: string;
  startDate: string | null;
  endDate: string | null;
  establishment: { id: string; slug: string; name: string };
  pwaSlug: string;
}
export interface CounterBatchSummary {
  id: string;
  number: number;
  kind: CounterKind;
  exhibitionSlug: string;
  exhibitionTitle: string;
  establishment: { id: string; slug: string; name: string };
  pwaSlug: string;
  quantity: number;
  note: string | null;
  createdBy: string;
  createdAt: string;
  counts: CounterCounts;
}
export interface CounterCodesOverview {
  kinds: { value: CounterKind; label: string }[];
  max: number;
  exhibitions: CounterExhibitionOption[];
  batches: CounterBatchSummary[];
}
export interface CounterCode {
  ticketId: string;
  n: number;
  value: string;
  url: string;
  state: CounterCodeState;
  activatedAt: string | null;
  device: string | null;
}
export interface CounterBatchDetail {
  id: string;
  number: number;
  kind: CounterKind;
  kindLabel: string;
  exhibition: { slug: string; title: string; startDate: string | null; endDate: string | null };
  establishment: { id: string; slug: string; name: string };
  pwaSlug: string;
  appUrl: string;
  quantity: number;
  note: string | null;
  createdBy: string;
  createdAt: string;
  counts: CounterCounts;
  codes: CounterCode[];
}
export interface CounterSummaryRow {
  kind: CounterKind;
  partner: string;
  establishmentId: string | null;
  exhibitionSlug: string | null;
  pwaSlug: string | null;
  printed: number;
  /** Dont codes unitaires (Achats et accès, V1.34). */
  unitary?: number;
  activated: number;
  activatedTotal: number;
  disabled: number;
}

export async function getCounterCodes(): Promise<CounterCodesOverview> {
  return request<CounterCodesOverview>("/admin/counter-codes");
}
export async function createCounterBatch(input: {
  exhibitionId: string;
  kind: CounterKind;
  quantity: number;
  note?: string;
}): Promise<{ id: string; number: number }> {
  return request("/admin/counter-codes", { method: "POST", body: JSON.stringify(input) });
}
export async function getCounterBatch(id: string): Promise<CounterBatchDetail> {
  return request<CounterBatchDetail>(`/admin/counter-codes/${encodeURIComponent(id)}`);
}
export type RevokeMotive = "perte" | "vol" | "autre";
export async function revokeCounterBatch(
  id: string,
  input: { motive: RevokeMotive; reason?: string; activatedSince?: string },
): Promise<{ revoked: number; free: number; activatedRevoked: number }> {
  return request(`/admin/counter-codes/${encodeURIComponent(id)}/revoke`, { method: "POST", body: JSON.stringify(input) });
}
export async function getCounterExhibitions(): Promise<Pick<CounterCodesOverview, "kinds" | "exhibitions">> {
  return request("/admin/counter-codes/exhibitions");
}
export async function revokeCounterCode(id: string, ticketId: string, reason?: string): Promise<{ revoked: number }> {
  return request(`/admin/counter-codes/${encodeURIComponent(id)}/codes/${encodeURIComponent(ticketId)}/revoke`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// =============================================================================
// Groupes Notre-Dame — facturation propre d'ALTO (02/11/2026)
// =============================================================================
// Cf. apps/api/src/routes/adminNdp.ts. Droit `ndp.manage`, verrouille au
// Super-admin : ces dossiers sont les clients d'ALTO, pas ceux d'un musee.

export type NdpState = "OPTION" | "CONFIRMED" | "CANCELLED";
export type NdpBillingFilter = "to-invoice" | "blocked" | "invoiced" | "to-refund";

export interface NdpDossierRow {
  dossier: string;
  state: NdpState;
  reservedOn: string | null;
  cancelledOn: string | null;
  visitOn: string;
  visitTime: string | null;
  visitLabel: string | null;
  articleCode: string | null;
  company: string | null;
  lastName: string | null;
  firstName: string | null;
  siret: string | null;
  postalCode: string | null;
  city: string | null;
  email: string | null;
  bookerType: string | null;
  targetAudience: string | null;
  totalTtcCents: number | null;
  paidTtcCents: number | null;
  invoicedAt: string | null;
  invoiceRef: string | null;
  creditNotedAt: string | null;
  refundedAt: string | null;
  blockedReason: string | null;
  lastSeenAt: string;
  lastSeenFile: string | null;
}

export interface NdpSummary {
  dossiers: { total: number; toInvoice: number; blocked: number; toRefund: number; invoiced: number };
  toInvoiceTotalCents: number;
  lastRun: {
    id: string;
    source: string;
    startedAt: string;
    ok: boolean;
    rowsTotal: number;
    created: number;
    updated: number;
    toInvoice: number;
    blocked: number;
  } | null;
}

export interface NdpAnomalyLine {
  dossier: string | null;
  code: string;
  detail: string;
  blocking: boolean;
}

export interface NdpRunRow {
  id: string;
  source: string;
  startedAt: string;
  applied: boolean;
  ok: boolean;
  rowsTotal: number;
  rowsSkipped: number;
  created: number;
  updated: number;
  unchanged: number;
  stale: number;
  toInvoice: number;
  blocked: number;
  toRefundOnly: number;
  toCreditAndRefund: number;
  disappeared: number;
  invoiceTotalCents: number;
  refundTotalCents: number;
  anomalies: NdpAnomalyLine[];
  warnings: string[];
  errors: string[];
}

export async function getNdpSummary(): Promise<NdpSummary> {
  return request<NdpSummary>("/admin/ndp/summary");
}

export async function getNdpDossiers(params: {
  state?: NdpState;
  billing?: NdpBillingFilter;
  q?: string;
  take?: number;
  skip?: number;
}): Promise<{ rows: NdpDossierRow[]; count: number; take: number; skip: number }> {
  const qs = new URLSearchParams();
  if (params.state) qs.set("state", params.state);
  if (params.billing) qs.set("billing", params.billing);
  if (params.q) qs.set("q", params.q);
  if (params.take) qs.set("take", String(params.take));
  if (params.skip) qs.set("skip", String(params.skip));
  const suffix = qs.toString();
  return request(`/admin/ndp/dossiers${suffix ? `?${suffix}` : ""}`);
}

export async function getNdpRuns(take = 10): Promise<{ runs: NdpRunRow[] }> {
  return request(`/admin/ndp/runs?take=${take}`);
}

/** Confirmer ou refuser l'identite legale de l'organisme d'un dossier. */
export async function decideNdpSiret(
  dossier: string,
  input: { action: "confirm" | "reject"; siret?: string; note?: string },
): Promise<{ ok: true; orgKey: string }> {
  return request(`/admin/ndp/dossiers/${encodeURIComponent(dossier)}/siret`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Emettre la facture. `validate` NUMEROTE dans la sequence legale : c'est
 * irreversible, d'ou la confirmation explicite exigee par le serveur.
 */
export async function emitNdpInvoice(
  dossier: string,
  input: { validate: boolean; sendEmail: boolean },
): Promise<
  | { ok: true; alreadyInvoiced: true; invoiceRef: string | null; invoicedAt: string }
  | { ok: true; alreadyInvoiced: false; result: { invoiceId: number; number: string | null; status: string | null; compliance: string[] } }
> {
  return request(`/admin/ndp/dossiers/${encodeURIComponent(dossier)}/invoice`, {
    method: "POST",
    body: JSON.stringify({ confirm: true, ...input }),
  });
}

/**
 * Avoir et remboursement d'un dossier annule (palier 2). `refunded` n'est vrai
 * que lorsque l'operateur atteste avoir rembourse depuis SogeCommerce : le
 * mouvement d'argent se fait la-bas, iPaidThat ne fait que l'enregistrer.
 */
export async function creditAndRefundNdp(
  dossier: string,
  input: { refunded: boolean },
): Promise<{
  ok: true;
  creditNote: string | null;
  refundRecorded: boolean;
  refundCents: number;
  todo: string[];
}> {
  return request(`/admin/ndp/dossiers/${encodeURIComponent(dossier)}/refund`, {
    method: "POST",
    body: JSON.stringify({ confirm: true, ...input }),
  });
}
